<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { required } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { SALES_DOC_MODULE } from "@/state/modules/salesDoc";
import { CATALOG_MODULE } from "@/state/modules/catalog";
import { LOOKUP_MODULE } from "@/state/modules/lookup";
import { toApiHeader, toApiLine, toApiDiscount } from "@/utils/salesDoc.mapper";
import {
  salesDocMethods,
  catalogMethods,
  lookupMethods,
  approvalMethods,
  configurationMethods,
  itemPriceMethods,
} from "@/state/helpers";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import SignaturePad from "@/components/SignaturePad";
import { getInlineSignatures } from "@/helpers/services/documentSignature.service";
import { getTaxes as getTaxesDirect, getDebtors as getDebtorsDirect } from "@/helpers/services/lookup.service";
import { getConfiguration as getConfigurationDirect } from "@/helpers/services/configuration.service";
import { subQuotationService } from "@/helpers/services/subQuotation.service";
import { selectQuotationOptions, getLastOrder } from "@/helpers/services/salesDoc.service";
import { paymentMethodService } from "@/helpers/services/paymentMethod.service";
import { sharedHelper } from "@/utils";

const DISCOUNT_MODES = [
  { value: "amount", text: "RM" },
  { value: "percent", text: "%" },
];

export default {
  page() {
    return {
      title: this.isEdit
        ? i18n.t("label.updateX", [i18n.t("module." + this.docType)])
        : i18n.t("label.createX", [i18n.t("module." + this.docType)]),
    };
  },
  components: { Layout, PageHeader, Multiselect, SignaturePad },
  data() {
    return {
      header: {
        DebtorCode: "",
        DocDate: new Date().toISOString().substr(0, 10),
        Description: "",
        CurrencyCode: "MYR",
        Agent: "",
        IssuedBy: "",
        ReceivedBy: "",
        IssuedBySignature: "",
        ReceivedBySignature: "",
        // One discount over the whole document, on top of the per-line ones.
        DocDiscountMode: "percent",
        DocDiscountValue: 0,
      },
      details: [this.makeEmptyLine()],
      discounts: [],
      // Inline-fetched copy of /lookup/taxes — independent of Vuex so the
      // totals card still populates if the global store action silently fails.
      fetchedTaxes: [],
      // True once the accounting system has answered the tax lookup — even
      // with nothing. Only then is an empty list a fact rather than a failure.
      taxesLoaded: false,
      // Per-item AutoCount price rows (one per UOM, each with Price 1–6),
      // keyed by item_code. Drives the UOM + price-tier dropdowns.
      itemPriceMap: {},
      discountModes: DISCOUNT_MODES,
      taxRate: 0,
      submitting: false,
      // The held approval this form was opened from, when it was.
      approval: null,
      decisionRemarks: "",
      decisionError: false,
      deciding: false,
      // Page-level loading overlay: true on initial load (lookups / existing doc)
      // and while the consignment balance loads after a debtor is picked.
      loading: true,
      validationErrors: [],
      lastLoadedDebtor: "",
      selectedAgent: null,
      selectedDebtor: null,
      // The customer picker searches the ledger as you type instead of
      // filtering a prefetched slice: the lookup only ever returns the first
      // 500 accounts by number, and there are more customers than that, so
      // anything sorting after the cut was unreachable from the form.
      // Its own list, not the shared debtorDdl, so searching here does not
      // empty the customer names other pages read out of the store.
      debtorOptions: [],
      debtorSearching: false,
      debtorSearchTimer: null,
      // Item picker modal state
      pickerTarget: null, // { kind: 'line' | 'discount', index: number }
      pickerSearch: "",
      // Quotation pricing binding choice
      priceBinding: "once", // 'once' | 'always' | 'min_qty'
      // Optional expiry for a non-once binding (Y-m-d). Blank = no expiry.
      priceBindingUntil: "",
      // True while an agent-side "Use this option" PATCH is in flight.
      optionSelecting: false,
      // If this quotation is closed (won/lost), the form becomes view-only.
      quotationLockedStatus: null, // null | 'won' | 'lost'
      // Inline signatures (customer + sales agent) — persisted via DocumentSignature table.
      sigCustomerName: "",
      sigCustomerData: "", // base64 PNG, no prefix
      sigCustomerExisting: "", // pre-filled image to show on edit before re-signing
      sigAgentName: "",
      sigAgentData: "",
      sigAgentExisting: "",
      // Sub-company quotations derived from this quotation (edit mode only).
      subQuotations: [],
      // ── Last order ──
      // The document the lines were filled from, so the form can say where
      // these prices came from and let them be cleared again.
      lastOrder: null,
      lastOrderLoading: false,
      // ── Payment ──
      // Shown after a cash sale has been created: the ledger issues the number,
      // so there is nothing to attach a payment to until the save comes back.
      showPayment: false,
      paymentMethods: [],
      paymentMethodsLoaded: false,
      selectedPaymentMethod: null,
      paymentDocNo: "",
      paymentAmount: null,
      paymentReference: "",
      paymentProofBase64: null,
      paymentSaving: false,
      paymentWalletBalance: null,
      subEditingUuid: null, // which sub is in inline price-edit mode
      subEditLines: [],
      subSaving: false,
    };
  },
  validations: {
    header: {
      DebtorCode: { required },
      DocDate: { required },
      Agent: { required },
    },
  },
  computed: {
    ...mapState(SALES_DOC_MODULE, { docDetails: "details" }),
    ...mapState(CATALOG_MODULE, ["canExchangeItems", "showingInSalesDocsItems"]),
    canExchangeSet() {
      return new Set(
        (this.canExchangeItems || [])
          .filter((c) => c.canExchange && c.itemCode)
          .map((c) => c.itemCode)
      );
    },
    showingInSalesDocsList() {
      return (this.showingInSalesDocsItems || []).filter(
        (c) => c.isShowingInSalesDocs && c.itemCode
      );
    },
    ...mapState(LOOKUP_MODULE, ["agentDdl", "debtorDdl", "itemDdl", "taxDdl"]),
    docType() { return this.$route.params.docType; },
    docNo() { return this.$route.params.docNo; },
    isEdit() { return !!this.docNo; },
    /** A held document: loaded from and saved back to its approval, not the ledger. */
    approvalUuid() { return this.$route.params.approvalUuid; },
    isPending() { return !!this.approvalUuid; },
    approvalIsOpen() { return this.isPending && this.approval && this.approval.status === "pending"; },
    canDecide() { return this.approvalIsOpen && this.$can("Update Approval"); },
    approvalChanges() { return (this.approval && this.approval.changes) || null; },
    isConsignment() { return this.docType === "consignment"; },
    isDelivery() { return this.docType === "deliveryorder" || this.docType === "deliveryreturn"; },
    isQuotation() { return this.docType === "quotation"; },
    isCashSale() { return this.docType === "cashsale"; },
    isDeliveryOrder() { return this.docType === "deliveryorder"; },
    today() { return new Date().toISOString().substr(0, 10); },
    /** Form is locked when: (a) it's a won/lost quotation OR
     *  (b) the doc is a delivery order being viewed from the listing.
     *  Delivery orders are auto-created from SO/SI and must be view-only. */
    isLocked() {
      // A decided approval is history; only an open one can still be changed.
      if (this.isPending) return !this.approvalIsOpen;
      return !!this.quotationLockedStatus || (this.isDeliveryOrder && this.isEdit);
    },
    title() {
      if (this.isPending) return i18n.t("label.pendingApproval") + " — " + i18n.t("module." + this.docType);
      return this.isEdit
        ? i18n.t("label.updateX", [i18n.t("module." + this.docType)])
        : i18n.t("label.createX", [i18n.t("module." + this.docType)]);
    },
    items() {
      return [
        { text: i18n.t("module.sales"), href: "/" },
        { text: i18n.t("module.salesDoc"), href: "/sales-docs" },
        { text: this.title, active: true },
      ];
    },
    /** Consignment uses (addReturnQty × unitPrice); others use (qty × unitPrice). */
    subTotal() {
      if (this.isConsignment) {
        return this.details.reduce(
          (s, l) => s + (Number(l.AddReturnQty) || 0) * (Number(l.UnitPrice) || 0),
          0
        );
      }
      return this.details.reduce(
        (s, l) => s + (Number(l.Qty) || 0) * (Number(l.UnitPrice) || 0),
        0
      );
    },
    discountTotal() {
      const st = this.subTotal;
      return this.discounts.reduce((sum, d) => {
        const v = Number(d.Value) || 0;
        if (d.Mode === "percent") return sum + (st * v) / 100;
        return sum + v;
      }, 0);
    },
    /**
     * Per-line discounts, in currency.
     *
     * Rounded per line, exactly as `SalesDocDiscountService` does on the server.
     * The two have to agree to the cent: what this card shows is what the
     * customer is told to pay, and what the server posts is what the ledger
     * records. A different rounding rule here would put a sen between them on
     * documents with awkward percentages.
     */
    lineDiscountTotal() {
      if (this.isConsignment) return 0;
      return this.details.reduce((s, l) => s + this.lineDiscountAmount(l), 0);
    },
    /** The document discount, taken off what the line discounts left. */
    docDiscountAmount() {
      if (this.isConsignment) return 0;
      const base = this.round2(Math.max(0, this.subTotal - this.lineDiscountTotal));
      const value = Number(this.header.DocDiscountValue) || 0;
      if (base <= 0 || value <= 0) return 0;
      const raw = this.header.DocDiscountMode === "amount" ? value : (base * value) / 100;
      return this.round2(Math.min(Math.max(raw, 0), base));
    },
    /** Everything taken off, whichever scheme put it there. */
    totalDiscount() {
      // discountTotal is the consignment rebate list; the other two are the
      // line and document discounts. A document only ever uses one scheme, so
      // these do not overlap.
      return this.round2(this.discountTotal + this.lineDiscountTotal + this.docDiscountAmount);
    },
    /** Base for tax = subTotal − every discount. */
    taxBase() {
      return Math.max(0, this.round2(this.subTotal - this.totalDiscount));
    },
    /** Active, non-zero supply tax codes — sourced from Vuex (`taxDdl`), but
     *  with an inline-fetched override (`fetchedTaxes`) used when the global
     *  lookup hasn't populated yet. Hardcoded Malaysian SST codes are the
     *  last-resort fallback so the totals card never looks empty when
     *  AutoCount has the codes seeded but the API path is failing for some
     *  reason. Edit `FALLBACK_TAXES` to change the defaults. */
    sourceTaxes() {
      const fromVuex = (this.taxDdl || []).filter((t) => Number(t.tax_rate) > 0 && t.is_active !== false);
      if (fromVuex.length > 0) return fromVuex;
      const fromFetch = (this.fetchedTaxes || []).filter((t) => Number(t.tax_rate) > 0 && t.is_active !== false);
      if (fromFetch.length > 0) return fromFetch;
      // The accounting system answered and has no tax codes: then there is no
      // tax, and nothing may be stamped on the lines. Posting the fallback's
      // "SR" to an AutoCount that has never heard of it fails every document.
      if (this.taxesLoaded) return [];
      // Hardcoded last-resort defaults so the UI never looks broken when the
      // lookup itself failed.
      return [
        { tax_code: 'SR',   tax_rate: 6,  is_active: true },
        { tax_code: 'SR-S', tax_rate: 8,  is_active: true },
        { tax_code: 'ST',   tax_rate: 10, is_active: true },
      ];
    },
    autoTaxes() {
      const base = this.taxBase;
      return this.sourceTaxes.map((t) => {
        const rate = Number(t.tax_rate) || 0;
        return { code: t.tax_code, rate, amount: base * rate / 100 };
      });
    },
    taxAmount() {
      if (this.autoTaxes.length > 0) {
        return this.autoTaxes.reduce((s, t) => s + t.amount, 0);
      }
      // Legacy fallback (global tax_rate from configurations).
      return this.taxBase * (Number(this.taxRate) || 0) / 100;
    },
    grandTotal() {
      // Tax now applies to every doc type, not just consignment.
      return this.round2(this.subTotal - this.totalDiscount + this.taxAmount);
    },
    /**
     * Every discount problem on the form right now.
     *
     * Drives the red borders, the messages under the fields, and whether Submit
     * can be pressed at all — one source, so the three cannot disagree.
     */
    discountProblems() {
      if (!this.canDiscount) return [];

      const problems = [];

      this.details.forEach((line, i) => {
        const message = this.lineDiscountError(line);
        if (message) {
          problems.push({ line: i, message: `${i18n.t("salesDoc.lineN", [i + 1])}: ${message}` });
        }
      });

      const docMessage = this.docDiscountError();
      if (docMessage) {
        problems.push({ line: null, message: `${i18n.t("salesDoc.docDiscount")}: ${docMessage}` });
      }

      return problems;
    },
    hasDiscountProblem() {
      return this.discountProblems.length > 0;
    },
    /** Whether this document kind takes line and document discounts. */
    canDiscount() {
      // Consignment prices its rebates per free item instead; the two schemes
      // do not compose, and the server refuses to mix them.
      return !this.isConsignment;
    },
    paymentMethodOptions() {
      return this.paymentMethods;
    },
    /** Only cash puts the money in this agent's own wallet. */
    paymentIsCash() {
      return this.selectedPaymentMethod?.type === "cash";
    },
    pickerFilteredItems() {
      const q = (this.pickerSearch || "").trim().toLowerCase();
      const list = this.itemDdl || [];
      if (!q) return list.slice(0, 200);
      return list.filter((it) =>
        (it.item_code || "").toLowerCase().includes(q) ||
        (it.description || "").toLowerCase().includes(q)
      ).slice(0, 200);
    },
  },
  watch: {
    "header.DebtorCode"(newVal) {
      if (!newVal || this.isEdit || newVal === this.lastLoadedDebtor) return;
      this.lastLoadedDebtor = newVal;

      if (this.isConsignment) {
        // A consignment sheet opens from the debtor's last sheet, not from a
        // previous order — its own mechanism, and they must not both run.
        this.loadConsignmentBalanceForDebtor(newVal);
        return;
      }

      this.loadLastOrderForDebtor(newVal);
    },
  },
  async created() {
    await this.loadPage();
    // The payment picker is part of the form now, so its options have to be
    // there before the agent reaches the bottom of it.
    if (this.isCashSale) await this.loadPaymentMethods();
  },
  methods: {
    ...salesDocMethods,
    ...catalogMethods,
    ...lookupMethods,
    ...approvalMethods,
    ...itemPriceMethods,
    /** Everything the page needs — lookups, then the document on edit. Also
     *  re-run after an agent-side option pick so the ledger line refreshes. */
    async loadPage() {
    this.loading = true;
    try {
    // Load lookups + tax rate
    const tasks = [
      this.lookupAgent({}),
      this.lookupDebtor({}).then(() => { this.debtorOptions = this.debtorDdl || []; }),
      this.lookupItem({}), this.lookupTax(),
      // Inline tax fetch — bypasses Vuex so the totals card populates even if
      // the global store action silently fails or runs out of order.
      (async () => {
        try {
          const r = await getTaxesDirect();
          this.fetchedTaxes = r?.data || [];
          this.taxesLoaded = true;
        } catch (_) { /* lookup failed — the hardcoded SST defaults below keep the totals card readable */ }
      })(),
    ];
    if (this.isConsignment) {
      tasks.push(this.getCanExchangeItems());
      tasks.push(this.getShowingInSalesDocsItems());
      tasks.push(this.loadTaxRate());
    }
    await Promise.all(tasks);

    // Prefill debtor from query string (e.g. quick action from Customer 360)
    if (!this.isEdit && this.$route.query.debtor) {
      this.header.DebtorCode = String(this.$route.query.debtor);
    }

    if (this.isEdit || this.isPending) {
      let r;
      if (this.isPending) {
        // The held payload is the document; it is in the bridge's PascalCase
        // shape, which the mapping below already reads.
        const a = await this.getApproval({ id: this.approvalUuid });
        this.approval = a?.data || null;
        r = { data: this.approval?.payload || {} };
      } else if (this.isConsignment) {
        r = await this.getConsignmentInventory({ docNo: this.docNo });
      } else {
        r = await this.getSalesDoc({ docType: this.docType, docNo: this.docNo });
      }
      const d = r?.data || {};
      // The API answers in snake_case now; the PascalCase reads are kept so a
      // response from an older backend still loads. Component state stays
      // PascalCase — renaming it would mean touching the whole template and
      // every validation rule for no gain.
      const h = d.header || d.Header || {};
      this.header = {
        DebtorCode: h.debtor_code || h.DebtorCode || d.debtor_code || "",
        DocDate: (h.doc_date || h.DocDate || d.doc_date || this.header.DocDate).substr(0, 10),
        Description: h.description || h.Description || d.description || "",
        CurrencyCode: h.currency_code || h.CurrencyCode || d.currency_code || "MYR",
        Agent: h.agent || h.Agent || d.agent || "",
        IssuedBy: h.issued_by || h.IssuedBy || "",
        ReceivedBy: h.received_by || h.ReceivedBy || "",
        IssuedBySignature: h.issued_by_signature || h.IssuedBySignature || "",
        ReceivedBySignature: h.received_by_signature || h.ReceivedBySignature || "",
        // The document discount as it was typed. Recorded by One Sales only —
        // AutoCount has no header discount field at all.
        DocDiscountMode: h.doc_discount_mode || "percent",
        DocDiscountValue: h.doc_discount_value ?? 0,
      };
      this.details = (d.details || d.Details || []).map((l) => ({
        ItemCode: l.ItemCode || l.item_code || "",
        Description: l.Description || l.description || "",
        Qty: l.Qty ?? l.qty ?? 1,
        UOM: l.UOM || l.uom || "",
        UnitPrice: l.UnitPrice ?? l.unit_price ?? 0,
        SubTotal: l.SubTotal ?? l.sub_total ?? 0,
        TaxCode: l.TaxCode || l.tax_code || "",
        OpeningQty: l.OpeningQty ?? l.opening_qty ?? 0,
        SoldQty: l.SoldQty ?? l.sold_qty ?? 0,
        AddReturnQty: l.AddReturnQty ?? l.add_return_qty ?? 0,
        BalanceQty: l.BalanceQty ?? l.balance_qty ?? 0,
        StarterQty: l.StarterQty ?? l.starter_qty ?? 0,
        ExchangeQty: l.ExchangeQty ?? l.exchange_qty ?? 0,
        // The discount as it was typed, which only One Sales records — the
        // ledger keeps the folded percentage. A line discounted in AutoCount
        // directly has no intent stored and opens at zero; its effect is still
        // in the ledger's own `discount` field and is not overwritten unless
        // the user edits this.
        DiscountMode: l.discount_mode || l.DiscountMode || "percent",
        DiscountValue: l.discount_value ?? l.DiscountValue ?? 0,
        options: [],
        showOptions: false,
        optionUuid: null,
        isSelected: false,
        selectedBy: null,
      }));
      if (!this.details.length) this.details = [this.makeEmptyLine()];
      if (this.isQuotation) this.hydrateLineOptions(d.line_options || []);
      this.mergeShowingItemsIntoDetails();
      this.ensureAllPriceRows();
      this.discounts = (d.discounts || d.Discounts || []).map((x) => ({
        Type: x.type || x.Type || "Discount",
        ItemCode: x.item_code || x.ItemCode || "",
        Qty: x.qty ?? x.Qty ?? 0,
        Mode: x.mode || x.Mode || "amount",
        Value: x.value ?? x.Value ?? 0,
      }));
      this.lastLoadedDebtor = this.header.DebtorCode;

      // If this is a closed quotation, the form is view-only.
      if (this.isQuotation) {
        const sr = await this.getQuotationStatus({ docNo: this.docNo });
        const sd = sr?.data || {};
        const st = sd.status || null;
        if (st === "won" || st === "lost") {
          this.quotationLockedStatus = st;
        }
        if (sd.price_binding) {
          this.priceBinding = sd.price_binding;
        }
        this.priceBindingUntil = sd.price_binding_until ? String(sd.price_binding_until).substr(0, 10) : "";
      }

      // Load derived sub-company quotations (quotation edit only).
      if (this.isQuotation) {
        this.loadSubQuotations();
      }

      // Pre-fill inline customer + agent signatures.
      try {
        const sigR = await getInlineSignatures(this.docType, this.docNo);
        // apiHelper unwraps response.data; sigR === { code, success, data: { customer, agent } }
        const map = sigR?.data || {};
        if (map.customer) {
          this.sigCustomerName = map.customer.signer_name || "";
          this.sigCustomerExisting = map.customer.signature_data || "";
        }
        if (map.agent) {
          this.sigAgentName = map.agent.signer_name || "";
          this.sigAgentExisting = map.agent.signature_data || "";
        }
      } catch (_) { /* non-critical */ }
    } else if (this.isConsignment) {
      // New consignment: pre-populate flagged items so admin's catalog is always visible.
      this.mergeShowingItemsIntoDetails();
    }
    const norm = (v) => (v || "").toString().trim().toUpperCase();
    const agentKey = norm(this.header.Agent);
    this.selectedAgent =
      (this.agentDdl || []).find((a) => norm(a.agent_code) === agentKey) || null;
    // If the doc references an agent code that isn't in the lookup (e.g. deactivated),
    // surface it anyway so it stays visible in the multiselect.
    if (!this.selectedAgent && this.header.Agent) {
      this.selectedAgent = { agent_code: this.header.Agent, agent_name: "" };
    }
    const debtorKey = norm(this.header.DebtorCode);
    this.selectedDebtor =
      (this.debtorDdl || []).find((d) => norm(d.account_no) === debtorKey) || null;
    if (!this.selectedDebtor && this.header.DebtorCode) {
      this.selectedDebtor = { account_no: this.header.DebtorCode, company_name: "" };
      this.resolveDebtorName(this.header.DebtorCode);
    }
    } finally {
      this.loading = false;
    }
    },
    /**
     * Attach the API's `line_options` to the lines. sort_order 0 is the line
     * that sits in the ledger (already loaded as the main line); every other
     * row for the same item becomes one of `line.options`.
     */
    hydrateLineOptions(lineOptions) {
      if (!Array.isArray(lineOptions) || !lineOptions.length) return;
      const byItem = {};
      lineOptions.forEach((o) => {
        const code = o.item_code || "";
        (byItem[code] = byItem[code] || []).push(o);
      });
      this.details.forEach((line) => {
        const rows = (byItem[line.ItemCode] || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        if (!rows.length) return;
        // The row marked selected IS the ledger line (after the customer or
        // the agent picks an alternative, the ledger is updated to it); the
        // original default is only a fallback for rows saved before any pick.
        const main = rows.find((r) => !!r.is_selected) || rows.find((r) => Number(r.sort_order) === 0) || null;
        if (main) {
          this.$set(line, "optionUuid", main.uuid || null);
          this.$set(line, "isSelected", !!main.is_selected);
          this.$set(line, "selectedBy", main.selected_by || null);
        }
        const alts = rows.filter((r) => r !== main).map((r) => ({
          UOM: r.uom || "",
          Qty: r.qty ?? 0,
          UnitPrice: r.unit_price ?? null,
          Discount: r.discount ?? 0,
          TaxCode: r.tax_code || "",
          Description: r.description || line.Description || "",
          uuid: r.uuid || null,
          isSelected: !!r.is_selected,
          selectedBy: r.selected_by || null,
        }));
        this.$set(line, "options", alts);
        this.$set(line, "showOptions", alts.length > 0);
      });
    },
    toggleOptions(line) {
      this.$set(line, "showOptions", !line.showOptions);
    },
    /** New alternative starts as a copy of the line's own offer. */
    addOption(line) {
      if (!Array.isArray(line.options)) this.$set(line, "options", []);
      line.options.push({
        UOM: line.UOM || "",
        Qty: line.Qty ?? 1,
        UnitPrice: line.UnitPrice ?? null,
        Discount: line.Discount ?? 0,
        TaxCode: line.TaxCode || "",
        Description: line.Description || "",
        uuid: null,
        isSelected: false,
        selectedBy: null,
      });
      this.$set(line, "showOptions", true);
    },
    removeOption(line, oi) {
      line.options.splice(oi, 1);
    },
    /** A line-shaped view of an option so the UOM / price-tier helpers apply. */
    optionCtx(line, opt) {
      return { ItemCode: line.ItemCode, UOM: opt.UOM, UnitPrice: opt.UnitPrice, boundPrice: null };
    },
    onOptionUomChange(opt, uom) {
      opt.UOM = uom || "";
      this.$set(opt, "UnitPrice", null);
    },
    onOptionTierChange(opt, tier) {
      this.$set(opt, "UnitPrice", tier ? Number(tier.value) : null);
    },
    optionOfferLabel(o) {
      const price = o.UnitPrice == null || o.UnitPrice === "" ? "—" : Number(o.UnitPrice).toFixed(2);
      return `${Number(o.Qty) || 0} ${o.UOM || ""} @ ${price}`;
    },
    selectedByLabel(by) {
      if (by === "customer") return i18n.t("quotation.selectedByCustomer");
      if (by === "agent") return i18n.t("quotation.selectedByAgent");
      return "";
    },
    /** Agent picks an offer for the ledger; the doc is reloaded on success. */
    async useOption(line, optionUuid) {
      if (!this.isEdit || !optionUuid || this.isLocked || this.optionSelecting) return;
      this.optionSelecting = true;
      try {
        await selectQuotationOptions(this.docNo, [{ item_code: line.ItemCode, option_uuid: optionUuid }]);
        await this.loadPage();
      } catch (e) {
        sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code);
      } finally {
        this.optionSelecting = false;
      }
    },
    async loadSubQuotations() {
      try {
        const r = await subQuotationService.getAll({ source_doc_no: this.docNo });
        this.subQuotations = r?.data || [];
      } catch (_) {
        this.subQuotations = [];
      }
    },
    startEditSub(sub) {
      this.subEditingUuid = sub.uuid;
      this.subEditLines = (sub.details || []).map((l) => ({ ...l }));
    },
    cancelEditSub() {
      this.subEditingUuid = null;
      this.subEditLines = [];
    },
    subEditTotal() {
      return this.subEditLines.reduce(
        (sum, l) => sum + (Number(l.qty) || 0) * (Number(l.unit_price) || 0),
        0
      );
    },
    async saveSub(sub) {
      // Qty / price can never go negative (same rule as the main form).
      if (this.subEditLines.some((l) => Number(l.unit_price) < 0 || Number(l.qty) < 0)) {
        return;
      }
      this.subSaving = true;
      try {
        await subQuotationService.update(sub.uuid, { details: this.subEditLines });
        this.cancelEditSub();
        await this.loadSubQuotations();
      } catch (e) {
        sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code);
      } finally {
        this.subSaving = false;
      }
    },
    async downloadSubPdf(sub) {
      try {
        const r = await subQuotationService.downloadPdf(sub.uuid);
        const blob = r instanceof Blob ? r : new Blob([r], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `quotation_${sub.doc_no}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code);
      }
    },
    onAgentChange(a) {
      this.selectedAgent = a;
      this.header.Agent = a ? a.agent_code : "";
    },
    onDebtorChange(d) {
      this.selectedDebtor = d;
      this.header.DebtorCode = d ? d.account_no : "";
      // Re-apply customer-specific pricing for lines that already have an item picked.
      if (this.header.DebtorCode) {
        this.details.forEach((line) => { if (line.ItemCode) this.applyCustomerPrice(line); });
      }
    },
    itemOptionFor(code) {
      return (this.itemDdl || []).find((i) => i.item_code === code) || null;
    },
    async onPickLineItem(line, item) {
      line.ItemCode = item ? item.item_code : "";
      if (!item) return;
      line.Description = item.description || line.Description || "";
      line.UOM = item.uom || line.UOM || "";
      if (this.isDelivery) {
        // Delivery follows the source doc's price — keep the base-price behaviour.
        line.UnitPrice = Number(item.unit_price) || 0;
        return;
      }
      // Tier-based docs: load the item's UOM/price rows. No default price is set —
      // the user picks a tier (quotation may then edit it); qty may apply a bound price.
      await this.ensurePriceRows(line.ItemCode);
      this.$set(line, "UnitPrice", null);
      // Default to the customer's bound price/UOM (from a won quotation) if any —
      // looked up across UOMs so e.g. a CARTON binding is picked up even though the
      // catalog sales UOM is REAM.
      await this.resolveBoundPrice(line, true);
    },
    /** Cache an item's AutoCount price rows (one per UOM, each with Price 1–6). */
    async ensurePriceRows(itemCode) {
      if (!itemCode || this.itemPriceMap[itemCode]) return;
      try {
        const ir = await this.getItemPriceByItem({ itemCode });
        this.$set(this.itemPriceMap, itemCode, ir?.data || []);
      } catch (_) {
        this.$set(this.itemPriceMap, itemCode, []);
      }
    },
    ensureAllPriceRows() {
      (this.details || []).forEach((l) => { if (l.ItemCode) this.ensurePriceRows(l.ItemCode); });
    },
    /** Distinct UOM options for a line's item. */
    uomOptionsFor(line) {
      return (this.itemPriceMap[line.ItemCode] || []).map((r) => r.uom).filter(Boolean);
    },
    /** Price-tier options ({label, value}) for the line's selected UOM.
     *  If the customer has a bound price, it's added as the first option so it can
     *  be shown/selected even when it isn't one of the standard Price 1–6 tiers. */
    priceTierOptionsFor(line) {
      const tiers = [];
      const seen = new Set();
      const add = (label, value) => {
        const v = Number(value);
        if (!(v > 0) || seen.has(v)) return;
        seen.add(v);
        tiers.push({ label, value: v });
      };
      if (!this.isQuotation && line.boundPrice != null) add(`Customer price — ${Number(line.boundPrice).toFixed(2)}`, line.boundPrice);
      const rows = this.itemPriceMap[line.ItemCode] || [];
      const row = rows.find((r) => (r.uom || "").toUpperCase() === (line.UOM || "").toUpperCase());
      if (row) {
        [row.price, row.price2, row.price3, row.price4, row.price5, row.price6].forEach((p, i) => {
          if (p != null && p !== "") add(`Price ${i + 1} — ${Number(p).toFixed(2)}`, p);
        });
      }
      // Always keep the line's current price selectable (e.g. an edit-loaded bound
      // or custom price that isn't one of the standard tiers) so it shows instead
      // of an empty "Select".
      if (line.UnitPrice != null) {
        const label = this.isQuotation
          ? `Custom — ${Number(line.UnitPrice).toFixed(2)}`
          : `Customer price — ${Number(line.UnitPrice).toFixed(2)}`;
        add(label, line.UnitPrice);
      }
      return tiers;
    },
    /** The tier object currently matching line.UnitPrice (for the dropdown value). */
    selectedTierFor(line) {
      return this.priceTierOptionsFor(line).find((t) => Number(t.value) === Number(line.UnitPrice)) || null;
    },
    onUomChange(line, uom) {
      line.UOM = uom || "";
      this.$set(line, "UnitPrice", null); // reset price when UOM changes
      this.applyCustomerPrice(line);
    },
    onTierChange(line, tier) {
      this.$set(line, "UnitPrice", tier ? Number(tier.value) : null);
      // Picking a price makes it the agent's. See resolveBoundPrice: without
      // this, a hand-picked price that happens to equal the old binding would
      // still read as untouched and be overwritten on the next lookup.
      this.$set(line, "appliedBoundPrice", null);
    },
    /**
     * Resolve the customer's bound price (from a won quotation) for a line.
     * anyUom=true (on item pick): look up the binding across UOMs and default the
     *   line to the bound UOM (e.g. CARTON) so the bound price is found.
     * anyUom=false (on qty/UOM change): look up for the line's current UOM only.
     * When a binding applies it sets boundPrice, which the price dropdown offers
     * as a labelled option; otherwise boundPrice is cleared. It fills UnitPrice
     * only while the line's price is still one this method put there — a price
     * the agent chose is never overwritten.
     */
    async resolveBoundPrice(line, anyUom) {
      // Bound customer prices apply to orders/invoices — never to quotations.
      // A quotation is where the price is defined, so re-applying a prior
      // binding here would be circular.
      if (this.isQuotation) { this.$set(line, "boundPrice", null); return; }
      if (!line.ItemCode || !this.header.DebtorCode) {
        this.$set(line, "boundPrice", null);
        return;
      }
      const qty = this.isConsignment
        ? Number(line.OpeningQty) || Number(line.AddReturnQty) || 0
        : Number(line.Qty) || 0;
      try {
        const cr = await this.getCustomerPrice({
          debtor_code: this.header.DebtorCode,
          item_code: line.ItemCode,
          uom: anyUom ? "" : (line.UOM || ""),
          qty,
        });
        const cp = cr?.data || null;
        if (cp) {
          if (anyUom && cp.uom) line.UOM = cp.uom;
          if (cp.price_applicable) {
            const bound = Number(cp.unit_price);
            this.$set(line, "boundPrice", bound);

            /*
             * The binding is an offer, not an override.
             *
             * This used to write `bound` onto the line every time it resolved —
             * and it resolves on a quantity change, a UOM change and a customer
             * change. So an agent who priced a cash sale by hand and then
             * corrected the quantity watched their price silently revert to the
             * quotation's. The document being sold is the one in front of them,
             * so its price wins.
             *
             * It still fills a line with no price of its own, and still follows
             * the binding as quantity crosses a min-qty tier — in both cases the
             * price on screen is one this put there. A price the agent chose is
             * left alone, and the binding stays available in the price dropdown,
             * labelled as the customer's.
             */
            const current = Number(line.UnitPrice);
            const untouched =
              line.UnitPrice == null ||
              current === 0 ||
              (line.appliedBoundPrice != null && current === Number(line.appliedBoundPrice));

            if (untouched) {
              this.$set(line, "UnitPrice", bound);
              this.$set(line, "appliedBoundPrice", bound);
            }
          } else {
            this.$set(line, "boundPrice", null);
          }
        } else {
          this.$set(line, "boundPrice", null);
        }
      } catch (_) { this.$set(line, "boundPrice", null); }
    },
    /** uom-specific bound-price resolution — used on qty / UOM change. */
    applyCustomerPrice(line) {
      return this.resolveBoundPrice(line, false);
    },
    onPickDiscountItem(disc, item) {
      disc.ItemCode = item ? item.item_code : "";
    },
    openItemPicker(kind, index) {
      this.pickerTarget = { kind, index };
      this.pickerSearch = "";
      this.$bvModal.show("itemPickerModal");
    },
    pickPickerItem(item) {
      if (!this.pickerTarget) return;
      const { kind, index } = this.pickerTarget;
      if (kind === "line") {
        const line = this.details[index];
        if (line) this.onPickLineItem(line, item);
      } else if (kind === "discount") {
        const d = this.discounts[index];
        if (d) this.onPickDiscountItem(d, item);
      }
      this.$bvModal.hide("itemPickerModal");
    },
    pickerLabelFor(code) {
      if (!code) return "";
      const it = (this.itemDdl || []).find((i) => i.item_code === code);
      return it ? `${it.item_code} — ${it.description || ''}` : code;
    },
    discountModeOption(value) {
      return this.discountModes.find((m) => m.value === value) || this.discountModes[0];
    },
    onDiscountModeChange(d, opt) {
      d.Mode = opt ? opt.value : "amount";
    },
    ...configurationMethods,
    async loadTaxRate() {
      // Bypass the Vuex store action — its catch handler pops a global alert
      // even when we'd rather silently fall back (the tax_rate config is
      // optional; if the row hasn't been seeded the user shouldn't see
      // "Configuration not found." every time they open a consignment).
      try {
        const r = await getConfigurationDirect("tax_rate");
        const v = r?.data?.value;
        if (v) this.taxRate = parseFloat(v) || 0;
      } catch (_) { this.taxRate = 0; }
    },
    /**
     * Search the ledger's customers as the user types.
     *
     * Debounced, because the picker fires on every keystroke and each call
     * reaches AutoCount. An empty box restores the opening list rather than
     * leaving the last search's results behind.
     */
    onDebtorSearch(query) {
      clearTimeout(this.debtorSearchTimer);
      const q = (query || "").trim();
      this.debtorSearchTimer = setTimeout(async () => {
        this.debtorSearching = true;
        try {
          // A typed search wants this search's hits, not the whole book.
          const r = await getDebtorsDirect(q || undefined, 50);
          this.debtorOptions = r?.data || [];
        } catch (_) {
          // Leave the last good list in place: an empty picker mid-typing
          // reads as "no such customer", which is worse than stale options.
        } finally {
          this.debtorSearching = false;
        }
      }, 300);
    },
    /**
     * The name behind a code the opening list did not carry.
     *
     * Editing a document whose customer sorts past the lookup's cut used to
     * show the bare account number, because the fallback stub is built with an
     * empty company name.
     */
    async resolveDebtorName(accountNo) {
      try {
        const r = await getDebtorsDirect(accountNo);
        const norm = (v) => String(v || "").trim().toUpperCase();
        const hit = (r?.data || []).find((d) => norm(d.account_no) === norm(accountNo));
        if (hit) {
          this.selectedDebtor = hit;
          if (!this.debtorOptions.some((d) => norm(d.account_no) === norm(accountNo))) {
            this.debtorOptions = [hit, ...this.debtorOptions];
          }
        }
      } catch (_) { /* the code alone still identifies the customer */ }
    },
    /** Mirrors Flutter loadConsignmentBalance: prefills lines from prior balance. */
    async loadConsignmentBalanceForDebtor(debtorCode) {
      if (!this.isConsignment || this.isEdit || !debtorCode) return;
      this.loading = true;
      try {
        const r = await this.getConsignmentBalance({ debtorCode });
        const balances = r?.data || [];
        if (balances.length) {
          this.details = balances.map((b) => ({
            ItemCode: b.ItemCode || "",
            Description: b.Description || b.ItemCode || "",
            OpeningQty: Number(b.TotalQty || 0),
            BalanceQty: Number(b.TotalQty || 0),
            UOM: b.UOM || (this.itemDdl || []).find((i) => i.item_code === b.ItemCode)?.uom || "",
            UnitPrice: Number(b.UnitPrice || 0),
            SoldQty: 0,
            AddReturnQty: 0,
            Qty: 0,
            SubTotal: 0,
            TaxCode: "",
          }));
          this.details.push(this.makeEmptyLine());
        }
        this.mergeShowingItemsIntoDetails();
      } catch (_) { /* no prior balance */ }
      finally { this.loading = false; }
    },
    /**
     * Merge admin-flagged showing items into the details list as new lines,
     * skipping ItemCodes already present. Empty placeholder lines are dropped
     * first so flagged items take their place. Always keeps at least one row.
     */
    mergeShowingItemsIntoDetails() {
      if (!this.isConsignment) return;
      const showing = this.showingInSalesDocsList;
      if (!showing.length) return;
      const existing = new Set(
        this.details
          .map((l) => (l.ItemCode || "").trim())
          .filter(Boolean)
      );
      this.details = this.details.filter(
        (l) => (l.ItemCode || "").trim() || (l.Description || "").trim()
      );
      showing.forEach((s) => {
        if (s.itemCode && !existing.has(s.itemCode)) {
          const it = (this.itemDdl || []).find((i) => i.item_code === s.itemCode);
          this.details.push({
            ...this.makeEmptyLine(),
            ItemCode: s.itemCode,
            Description: s.description || "",
            UOM: (it && it.uom) || "",
          });
        }
      });
      if (!this.details.length) this.details = [this.makeEmptyLine()];
      this.ensureAllPriceRows();
    },
    makeEmptyLine() {
      return {
        ItemCode: "", Description: "", Qty: 1, UOM: "", UnitPrice: null, SubTotal: 0, TaxCode: "", boundPrice: null,
        OpeningQty: 0, SoldQty: 0, AddReturnQty: 0, BalanceQty: 0, StarterQty: 0, ExchangeQty: 0,
        // The line's own discount, as the user types it.
        DiscountMode: "percent", DiscountValue: 0,
        // The bound price this line was last given, so a later lookup can tell
        // a price it set from one the agent chose. Transient; never posted.
        appliedBoundPrice: null,
        // Quotation-only alternative offers (see hydrateLineOptions).
        options: [], showOptions: false, optionUuid: null, isSelected: false, selectedBy: null,
      };
    },

    // ── Discounts ──────────────────────────────────────────────────────────

    round2(v) {
      return Math.round((Number(v) || 0) * 100) / 100;
    },
    /** A line's gross, before any discount. */
    lineGross(line) {
      return this.round2((Number(line.Qty) || 0) * (Number(line.UnitPrice) || 0));
    },
    /**
     * A line's own discount in currency, clamped to the line.
     *
     * Clamped rather than validated away so the card never shows a negative
     * line while the user is mid-typing; the server refuses a percentage over
     * 100 on submit, which is where the mistake is worth reporting.
     */
    lineDiscountAmount(line) {
      const gross = this.lineGross(line);
      const value = Number(line.DiscountValue) || 0;
      if (gross <= 0 || value <= 0) return 0;
      const raw = line.DiscountMode === "amount" ? value : (gross * value) / 100;
      return this.round2(Math.min(Math.max(raw, 0), gross));
    },
    /** What the line is worth after its own discount — the Amount column. */
    lineNet(line) {
      return this.round2(this.lineGross(line) - this.lineDiscountAmount(line));
    },
    setLineDiscountMode(line, mode) {
      this.$set(line, "DiscountMode", mode);
    },
    /**
     * What is wrong with a discount, in words, or null when nothing is.
     *
     * Returns the message rather than a boolean because it is shown under the
     * field the user is typing in. A discount that cannot mean what it says is
     * caught here, while they can still see what they typed — not after a round
     * trip to the server, by which point the form has been submitted and the
     * answer arrives as an alert over the top of it.
     *
     * @param {number} value  as typed
     * @param {string} mode   'percent' | 'amount'
     * @param {number} base   what the discount comes off
     */
    discountError(value, mode, base) {
      const v = Number(value) || 0;

      if (v < 0) return i18n.t("salesDoc.discountNegative");
      if (v === 0) return null;

      if (mode === "percent" && v > 100) return i18n.t("salesDoc.discountOverLimit");

      // Nothing to check against yet — a line with no price is incomplete, not
      // over-discounted, and flagging it would put a red box on every new row.
      if (base <= 0) return null;

      const taken = mode === "amount" ? v : (base * v) / 100;

      // A document that nets nothing is not a sale. Caught per line and on the
      // document total, so a single zeroed line is refused even when the rest
      // of the document still carries a value.
      if (this.round2(base - taken) <= 0) return i18n.t("salesDoc.discountToZero");

      return null;
    },
    /** A line's discount error, measured against that line's own gross. */
    lineDiscountError(line) {
      return this.discountError(line.DiscountValue, line.DiscountMode, this.lineGross(line));
    },
    /** The document discount's error, measured against what the lines left. */
    docDiscountError() {
      return this.discountError(
        this.header.DocDiscountValue,
        this.header.DocDiscountMode,
        this.round2(Math.max(0, this.subTotal - this.lineDiscountTotal))
      );
    },
    setDocDiscountMode(mode) {
      this.$set(this.header, "DocDiscountMode", mode);
    },

    // ── Last order ─────────────────────────────────────────────────────────

    /**
     * Fill the lines from what this customer bought last time.
     *
     * Only on a new document, and only over lines the user has not started
     * filling in: overwriting typed work because a customer was picked a second
     * time would lose the thing they were in the middle of.
     */
    /** A line the user has actually put something on. */
    lineHasContent(line) {
      return !!((line.ItemCode || "").trim() || (line.Description || "").trim());
    },
    async loadLastOrderForDebtor(debtorCode) {
      if (!debtorCode || this.isEdit || this.isPending || this.isLocked) return;
      if (this.isConsignment) return; // consignment opens from its own last sheet

      this.lastOrderLoading = true;
      try {
        const r = await getLastOrder(debtorCode, { types: "cashsale,salesinvoice" });
        const data = r?.data;

        if (!data || !(data.details || []).length) {
          this.lastOrder = null;
          return;
        }

        /*
         * Added to what is on the form, not swapped for it.
         *
         * The two are different things: the lines the agent typed are what
         * they are selling right now, and last time's order is a reminder of
         * what else this customer usually takes. An earlier version replaced
         * the lines outright and so had to refuse to run at all once anything
         * had been typed — which meant picking the customer second silently
         * cost you the feature.
         *
         * An item already on the form is skipped rather than added twice, and
         * the row the agent typed is the one that survives: they chose that
         * quantity and price for today.
         */
        const kept = this.details.filter((l) => this.lineHasContent(l));
        const onForm = new Set(
          kept.map((l) => (l.ItemCode || "").trim().toUpperCase()).filter(Boolean)
        );

        const added = data.details
          .filter((row) => {
            const code = (row.item_code || "").trim().toUpperCase();
            return !code || !onForm.has(code);
          })
          .map((row) => ({
            ...this.makeEmptyLine(),
            ItemCode: row.item_code || "",
            Description: row.description || "",
            Qty: Number(row.qty) || 1,
            UOM: row.uom || "",
            UnitPrice: row.unit_price ?? null,
            TaxCode: row.tax_code || "",
            // The discount as it was typed then, where One Sales recorded it.
            // Falling back to no discount rather than to the ledger's folded
            // percentage: re-applying that on top of a fresh price would
            // discount the new document by an amount nobody chose.
            DiscountMode: row.discount_mode || "percent",
            DiscountValue: Number(row.discount_value) || 0,
            // Marks the row as carried in, so it can be taken back out again
            // without touching what the agent typed. Transient; never posted.
            fromLastOrder: true,
          }));

        // Everything was already on the form — nothing was carried in, so
        // saying a document was loaded would be a claim about lines that are
        // the agent's own.
        if (!added.length) {
          this.lastOrder = null;
          return;
        }

        this.details = [...kept, ...added];

        this.lastOrder = {
          docNo: data.doc_no,
          docType: data.doc_type,
          docDate: data.doc_date,
          count: added.length,
        };

        // The UOM/price dropdowns are driven by itemPriceMap, which is keyed by
        // item code and has to be loaded for the codes just pulled in.
        this.ensureAllPriceRows();
      } catch (_) {
        // A customer with no history is the normal case, not an error.
        this.lastOrder = null;
      } finally {
        this.lastOrderLoading = false;
      }
    },
    /**
     * Take back the carried-in lines, leaving the agent's own alone.
     *
     * Only the rows this brought in are removed — resetting the whole table
     * would throw away work the agent did before picking the customer, which
     * is the thing the merge above exists to protect.
     */
    clearLastOrder() {
      const mine = this.details.filter((l) => !l.fromLastOrder);
      this.details = mine.length ? mine : [this.makeEmptyLine()];
      this.lastOrder = null;
    },

    // ── Payment ────────────────────────────────────────────────────────────

    async loadPaymentMethods() {
      if (this.paymentMethodsLoaded) return;
      try {
        const r = await paymentMethodService.getAll();
        this.paymentMethods = (r?.data || []).map((m) => ({
          uuid: m.uuid,
          name: m.name,
          type: m.type,
          details: Array.isArray(m.details) ? m.details : [],
          instructions: m.instructions || "",
          imageUrl: m.image_url || null,
        }));
      } catch (_) {
        this.paymentMethods = [];
      } finally {
        this.paymentMethodsLoaded = true;
        if (!this.selectedPaymentMethod) {
          // Cash first when there is one: it is what happens at a counter.
          this.selectedPaymentMethod =
            this.paymentMethods.find((m) => m.type === "cash") || null;
        }
      }
    },
    /** Opened with the created document's number, once the ledger has answered. */
    onPaymentMethodChange(m) {
      this.selectedPaymentMethod = m;
    },
    onPaymentProofSelected(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => { this.paymentProofBase64 = reader.result; };
      reader.readAsDataURL(file);
    },
    addLine() { this.details.push(this.makeEmptyLine()); },
    removeLine(i) { this.details.splice(i, 1); },

    tallyOkFor(line) {
      const starter = Number(line.StarterQty) || 0;
      const exchange = Number(line.ExchangeQty) || 0;
      const sold = Number(line.SoldQty) || 0;
      return Math.abs((starter + exchange) - sold) < 0.000001;
    },

    /**
     * The FIRST consignment for a debtor (no line carries an opening balance) is
     * just the initial placement, so balance = Restock (Sold is not subtracted).
     * From the 2nd consignment onward, balance = Open + Restock − Sold, where
     * Open is the prior doc's balance.
     */
    isFirstConsignmentDoc() {
      return this.isConsignment && !this.details.some((l) => (Number(l.OpeningQty) || 0) > 0);
    },
    balanceFor(line) {
      const opening = Number(line.OpeningQty) || 0;
      const restock = Number(line.AddReturnQty) || 0;
      const sold = Number(line.SoldQty) || 0;
      if (this.isFirstConsignmentDoc()) return restock;
      return opening + restock - sold;
    },

    addDiscount() {
      this.discounts.push({ Type: "Discount", ItemCode: "", Qty: 0, Mode: "amount", Value: 0 });
    },
    removeDiscount(i) { this.discounts.splice(i, 1); },

    /** Returns full list of validation errors (mirrors Flutter validateForm). */
    validateForm() {
      const errors = [];
      if (!this.header.DebtorCode.trim()) errors.push(i18n.t("label.debtorCode") + " is required");
      if (!this.header.Agent.trim()) errors.push(i18n.t("label.agent") + " is required");
      if (!this.header.DocDate.trim()) errors.push(i18n.t("label.docDate") + " is required");

      // Already shown under each offending field and already blocking Submit;
      // repeated here so the summary alert agrees with the fields, and so a
      // document that somehow reaches submit cannot get past this.
      this.discountProblems.forEach((p) => errors.push(p.message));

      if (this.isConsignment) {
        const valid = this.details.filter((l) => (l.ItemCode || "").trim());
        if (!valid.length) {
          errors.push("At least one item must be selected");
        } else {
          const hasMovement = valid.some(
            (l) => Number(l.AddReturnQty) > 0 || Number(l.SoldQty) > 0
          );
          if (!hasMovement) errors.push("Enter SOLD or ADD/(RET) quantity for at least one item");
          this.details.forEach((l, i) => {
            if (!(l.ItemCode || "").trim()) return;
            // Negative-balance guard only applies from the 2nd consignment
            // onward (Open > 0 = a carried-forward balance). On the first pickup
            // (Open = 0) the balance is simply the Restock, so skip the check.
            if ((Number(l.OpeningQty) || 0) > 0 && this.balanceFor(l) < 0) {
              errors.push(`Line ${i + 1} (${l.ItemCode}): balance cannot be negative`);
            }

            // Starter + Exchange tally for can-exchange products with Sold>0.
            // Skipped on the first consignment — nothing has been sold yet.
            if (!this.isFirstConsignmentDoc() && this.canExchangeSet.has(l.ItemCode) && Number(l.SoldQty) > 0 && !this.tallyOkFor(l)) {
              const starter = Number(l.StarterQty) || 0;
              const exchange = Number(l.ExchangeQty) || 0;
              const sold = Number(l.SoldQty) || 0;
              errors.push(`Line ${i + 1} (${l.ItemCode}): Starter (${starter}) + Exchange (${exchange}) must equal Sold (${sold})`);
            }
          });
        }
      } else {
        const hasLine = this.details.some(
          (l) => (l.ItemCode || "").trim() || (l.Description || "").trim()
        );
        if (!hasLine) errors.push("At least one line item is required");
      }

      // Numeric fields cannot be negative.
      this.details.forEach((l, i) => {
        const hasIdentity = (l.ItemCode || "").trim() || (l.Description || "").trim();
        if (!hasIdentity) return;
        const negFields = [];
        if (Number(l.Qty) < 0) negFields.push("Qty");
        if (Number(l.OpeningQty) < 0) negFields.push("Opening");
        if (Number(l.SoldQty) < 0) negFields.push("Sold");
        if (Number(l.AddReturnQty) < 0) negFields.push("Add/Return");
        if (Number(l.UnitPrice) < 0) negFields.push("Unit Price");
        if (negFields.length) {
          errors.push(`Line ${i + 1} (${l.ItemCode || l.Description}): ${negFields.join(", ")} cannot be negative`);
        }
      });
      this.discounts.forEach((d, i) => {
        if (Number(d.Qty) < 0) errors.push(`Discount line ${i + 1}: Qty cannot be negative`);
      });

      // Quotation alternatives: non-negative, and no two offers (main line
      // included) with the same UOM + price — that would be the same offer twice.
      if (this.isQuotation) {
        this.details.forEach((l, i) => {
          const opts = Array.isArray(l.options) ? l.options : [];
          if (!opts.length) return;
          const key = (o) => `${(o.UOM || "").toUpperCase()}|${Number(o.UnitPrice) || 0}`;
          const seen = new Set([key(l)]);
          opts.forEach((o, oi) => {
            const tag = `Line ${i + 1} (${l.ItemCode || l.Description}) option ${oi + 1}`;
            if (Number(o.Qty) < 0) errors.push(`${tag}: Qty cannot be negative`);
            if (Number(o.UnitPrice) < 0) errors.push(`${tag}: Unit Price cannot be negative`);
            if (o.UnitPrice == null || o.UnitPrice === "") errors.push(`${tag}: Unit Price is required`);
            const k = key(o);
            if (seen.has(k)) errors.push(`${tag}: ${i18n.t("quotation.optionDuplicate")}`);
            seen.add(k);
          });
        });
      }

      return errors;
    },

    async submit() {
      if (this.isLocked) return;
      this.$v.header.$touch();
      this.validationErrors = this.validateForm();
      if (this.$v.header.$invalid || this.validationErrors.length) return;

      // Submit list = "validation list". For consignment, drop lines where the
      // agent never touched anything (no opening balance, no sold, no add/return)
      // so admin-flagged display-only rows aren't persisted.
      const submitFilter = (l) => {
        const hasIdentity =
          (l.ItemCode || "").trim() || (l.Description || "").trim();
        if (!hasIdentity) return false;
        if (!this.isConsignment) return true;
        const opening = Number(l.OpeningQty) || 0;
        const sold = Number(l.SoldQty) || 0;
        const addRet = Number(l.AddReturnQty) || 0;
        return opening !== 0 || sold !== 0 || addRet !== 0;
      };

      // AutoCount stores tax per line. The form auto-applies every active
      // non-zero tax code to the doc-level totals, but only one code can be
      // written to a line — we use the first non-zero code for persistence.
      const primaryTaxCode = (this.autoTaxes[0] && this.autoTaxes[0].code) || "";
      // A code the accounting system does not have can never post. When it
      // answered the lookup with nothing, lines go out with no code — including
      // a held document that was captured with one before this was known.
      const noTaxCodes = this.taxesLoaded && this.sourceTaxes.length === 0;
      const lineTaxCode = (own) => (noTaxCodes ? "" : (primaryTaxCode || own || ""));
      const payload = {
        header: toApiHeader({ ...this.header, InclusiveTax: false }),
        details: this.details
          .filter(submitFilter)
          .map((l) => {
            // For consignment, AutoCount only persists the standard `Qty` field
            // — OpeningQty/SoldQty/AddReturnQty are app-only. The movement the
            // user actually entered lives in AddReturnQty, so we mirror it into
            // Qty before persistence; otherwise the doc reloads as Qty=1 (the
            // default) and the user sees their values mangled (e.g. "+Ret 15"
            // coming back as "+Ret 1").
            const persistedQty = this.isConsignment
              ? (Number(l.AddReturnQty) || 0)
              : (Number(l.Qty) || 0);
            return toApiLine({
              ...l,
              // Alternatives are a quotation feature; other docs never post them.
              options: this.isQuotation
                ? (l.options || []).map((o) => ({
                    UOM: o.UOM || "",
                    Qty: Number(o.Qty) || 0,
                    UnitPrice: Number(o.UnitPrice) || 0,
                    Discount: Number(o.Discount) || 0,
                    TaxCode: lineTaxCode(o.TaxCode),
                    Description: o.Description || l.Description || "",
                  }))
                : [],
              Qty: persistedQty,
              TaxCode: lineTaxCode(l.TaxCode),
              SubTotal: this.isConsignment
                ? (Number(l.AddReturnQty) || 0) * (Number(l.UnitPrice) || 0)
                : (Number(l.Qty) || 0) * (Number(l.UnitPrice) || 0),
              BalanceQty: this.balanceFor(l),
            });
          }),
        discounts: this.discounts
          .filter((d) => (d.ItemCode || "").trim() || Number(d.Value) !== 0)
          .map(toApiDiscount),
      };
      // Quotation pricing binding — sent on both create and edit so admin can
      // switch the binding choice at any time before the quotation is closed.
      if (this.isQuotation) {
        payload.price_binding = this.priceBinding;
        // Expiry only means something when prices carry forward.
        payload.price_binding_until =
          this.priceBinding !== "once" && this.priceBindingUntil ? this.priceBindingUntil : null;
      }

      /*
       * How the customer paid, travelling with the document.
       *
       * Asked after the save at first, which only ever worked in auto mode:
       * under manual approval a cash sale produces an approval request and no
       * document, so the question was never put. It rides in the payload like
       * the signature below and is recorded when the document posts — which for
       * a held document is whenever somebody approves it.
       */
      if (this.isCashSale && this.selectedPaymentMethod) {
        payload.payment_method_uuid = this.selectedPaymentMethod.uuid;
        payload.payment_amount = Number(this.paymentAmount) || this.grandTotal;
        payload.payment_reference = this.paymentReference || null;
        payload.payment_proof_base64 = this.paymentProofBase64 || null;
      }

      // The sales agent's signature travels with the document rather than in a
      // second call afterwards. Approval mode can hold the document, and a held
      // document has no number to attach a signature to — sending it here means
      // it is stored with the payload and lands when the document posts.
      // (The customer signs through the external share link, not this form.)
      if (this.sigAgentData || this.sigAgentName) {
        payload.agent_name = this.sigAgentName || null;
        payload.agent_signature_data = this.sigAgentData || null;
      }

      this.submitting = true;
      try {
        // A held document is saved back to its approval; the ledger is only
        // written when somebody approves.
        if (this.isPending) {
          const saved = await this.updateApprovalPayload({ id: this.approvalUuid, data: { payload } });
          if (saved) this.$router.push({ name: "Sales-doc-listing" });
          return;
        }

        const r = this.isEdit
          ? await this.updateSalesDoc({ docType: this.docType, docNo: this.docNo, data: payload })
          : await this.createSalesDoc({ docType: this.docType, data: payload, filter: {} });

        // A 202 means the document was held for approval and exists nowhere
        // yet, so its "doc no" is only a placeholder — send the user to the
        // queue it is actually sitting in.
        if (r?.pending_approval || r?.is_exceed_limit) {
          this.$router.push({ name: "Approval-listing" });
          return;
        }

        this.$router.push({ name: "Sales-doc-listing" });
      } catch (_) { /* alert already shown by store */ }
      finally { this.submitting = false; }
    },

    /**
     * The document number out of a create response, whatever shape it came in.
     *
     * Mirrors the server's `Controller::docNoOf()` — the adapter answers
     * `{doc_no, raw}` while the bridge client answers its own `{docNo}`, and
     * reading only one of the two spellings was already a live bug once.
     */
    docNoOf(r) {
      const data = r?.data;
      if (typeof data === "string") return data || null;
      for (const level of [data, data?.raw, r]) {
        if (!level) continue;
        for (const key of ["doc_no", "docNo", "DocNo"]) {
          if (level[key]) return String(level[key]);
        }
      }
      return null;
    },

    cancel() { this.$router.push({ name: "Sales-doc-listing" }); },
    showChange(v) { return v === null || v === undefined || v === "" ? "—" : v; },

    /** Approve posts the held document to the accounting system, as it is on screen. */
    async approveHeld() {
      if (this.deciding) return;
      this.deciding = true;
      try {
        const r = await this.approveApproval({ id: this.approvalUuid, data: { remarks: this.decisionRemarks }, filter: {} });
        // A refusal from the accounting system is shown by the store; stay so it can be retried.
        if (r) this.$router.push({ name: "Sales-doc-listing" });
      } finally {
        this.deciding = false;
      }
    },
    async rejectHeld() {
      if (!this.decisionRemarks || !this.decisionRemarks.trim()) { this.decisionError = true; return; }
      this.decisionError = false;
      if (this.deciding) return;
      this.deciding = true;
      try {
        const r = await this.rejectApproval({ id: this.approvalUuid, data: { remarks: this.decisionRemarks, rejection_reason: this.decisionRemarks }, filter: {} });
        if (r) this.$router.push({ name: "Sales-doc-listing" });
      } finally {
        this.deciding = false;
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <!-- Where this document actually is. -->
    <b-alert v-if="isPending && approval" show :variant="approval.status === 'rejected' ? 'danger' : (approval.status === 'approved' ? 'success' : 'warning')" class="d-flex align-items-start">
      <i class="mdi mdi-information-outline font-size-18 mr-2"></i>
      <div>
        <div v-if="approval.status === 'pending'">{{ $t('label.heldDocument') }}</div>
        <div v-else-if="approval.status === 'rejected'">{{ $t('label.rejectedDocument') }}<span v-if="approval.rejection_reason"> — {{ approval.rejection_reason }}</span></div>
        <div v-else>{{ $t('label.posted') }} — {{ approval.doc_no }}</div>
        <small v-if="approval.remarks" class="text-muted d-block">{{ approval.remarks }}</small>
        <!-- What changed: the live document vs this edit, or what the
             requester sent vs what an approver made of it. -->
        <div v-if="approvalChanges" class="mt-2 small">
          <div class="font-weight-bold mb-1">{{ approval.action === 'update' ? $t('label.whatChanges') : $t('label.whatApproverChanged') }}</div>
          <div v-if="!approvalChanges.header.length && !approvalChanges.lines.length" class="text-muted">{{ $t('label.noChangesDetected') }}</div>
          <div v-for="(c, i) in approvalChanges.header" :key="'h' + i">
            <span class="text-muted">{{ c.field }}:</span> <del class="text-muted">{{ showChange(c.before) }}</del> → <b>{{ showChange(c.after) }}</b>
          </div>
          <div v-for="(l, i) in approvalChanges.lines" :key="'l' + i" class="mt-1">
            <b-badge :variant="{ added: 'success', removed: 'danger', modified: 'warning' }[l.change] || 'secondary'">{{ $t('label.line' + l.change.charAt(0).toUpperCase() + l.change.slice(1)) }}</b-badge>
            <b class="ml-1">{{ l.item_code || l.description }}</b>
            <span v-for="(f, j) in l.fields" :key="'f' + j" class="ml-2">
              <span class="text-muted">{{ f.field }}:</span> <del class="text-muted">{{ showChange(f.before) }}</del> → <b>{{ showChange(f.after) }}</b>
            </span>
          </div>
        </div>
      </div>
    </b-alert>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-overlay :show="loading" rounded="sm" spinner-variant="primary" :opacity="0.6">
            <b-alert v-if="validationErrors.length" variant="danger" show class="mb-3">
              <ul class="mb-0">
                <li v-for="(e, i) in validationErrors" :key="i">{{ e }}</li>
              </ul>
            </b-alert>

            <b-alert v-if="isLocked" variant="warning" show class="mb-3">
              <i class="mdi mdi-lock-outline mr-1"></i>
              <template v-if="isDeliveryOrder">
                This delivery order is view-only. Delivery orders are generated from the sales order or invoice they belong to and cannot be edited here.
              </template>
              <template v-else>
                {{ $t('quotation.lockedBanner', [$t('quotation.status.' + quotationLockedStatus)]) }}
              </template>
            </b-alert>

            <b-form @submit.prevent="submit">
              <!-- Header -->
              <h6 class="text-primary">Header</h6>
              <b-row>
                <b-col cols="12"><b-form-group :label="$t('label.debtorCode')" :state="$v.header.DebtorCode.$dirty ? !$v.header.DebtorCode.$error : null">
                  <multiselect
                    :value="selectedDebtor"
                    :options="debtorOptions"
                    track-by="account_no"
                    :custom-label="(d) => `${d.account_no} — ${d.company_name || ''}`"
                    :show-labels="false"
                    :allow-empty="false"
                    :internal-search="false"
                    :loading="debtorSearching"
                    open-direction="below"
                    :placeholder="$t('label.searchCustomer')"
                    @search-change="onDebtorSearch"
                    @input="onDebtorChange"
                  />
                </b-form-group></b-col>
                <b-col cols="12"><b-form-group :label="$t('label.docDate')">
                  <b-form-input type="date" v-model="header.DocDate" :state="$v.header.DocDate.$dirty ? !$v.header.DocDate.$error : null" />
                </b-form-group></b-col>
                <b-col cols="12"><b-form-group :label="$t('label.agent')" :state="$v.header.Agent.$dirty ? !$v.header.Agent.$error : null" :invalid-feedback="$t('label.agent') + ' is required'">
                  <multiselect
                    :value="selectedAgent"
                    :options="agentDdl"
                    track-by="agent_code"
                    :custom-label="(a) => `${a.agent_code} — ${a.agent_name || ''}`"
                    :show-labels="false"
                    :allow-empty="false"
                    :placeholder="$t('label.select')"
                    :class="{ 'is-invalid': $v.header.Agent.$dirty && $v.header.Agent.$error }"
                    @input="onAgentChange"
                  />
                </b-form-group></b-col>
                <b-col cols="12"><b-form-group :label="$t('label.currency')">
                  <b-form-input v-model="header.CurrencyCode" />
                </b-form-group></b-col>
                <b-col cols="12"><b-form-group :label="$t('label.description')">
                  <b-form-input v-model="header.Description" />
                </b-form-group></b-col>
              </b-row>

              <!-- Lines -->
              <h6 class="text-primary mt-3">Lines</h6>

              <!-- Filled from the customer's last order. Says which document and
                   when, because these are that day's prices rather than today's. -->
              <b-alert v-if="lastOrderLoading" show variant="light" class="py-2 px-3 mb-2">
                <b-spinner small class="mr-2" />
                <small>{{ $t('salesDoc.lastOrderLoading') }}</small>
              </b-alert>
              <b-alert
                v-else-if="lastOrder"
                show
                variant="info"
                class="py-2 px-3 mb-2 d-flex justify-content-between align-items-center flex-wrap"
              >
                <div>
                  <i class="mdi mdi-history mr-1"></i>
                  <strong class="small">
                    {{ $t('salesDoc.lastOrderTitle', [lastOrder.count, lastOrder.docNo, lastOrder.docDate || '—']) }}
                  </strong>
                  <div class="small text-muted">{{ $t('salesDoc.lastOrderHint') }}</div>
                </div>
                <b-button size="sm" variant="outline-secondary" :disabled="isLocked" @click="clearLastOrder">
                  <i class="mdi mdi-close mr-1"></i>{{ $t('salesDoc.lastOrderUndo') }}
                </b-button>
              </b-alert>

              <b-table-simple small responsive>
                <b-thead>
                  <b-tr>
                    <b-th>{{ $t('label.itemCode') }}</b-th>
                    <b-th v-if="!isConsignment">{{ $t('label.description') }}</b-th>
                    <b-th v-if="!isConsignment">{{ $t('label.qty') }}</b-th>
                    <b-th v-if="isConsignment">{{ $t('label.openQty') }}</b-th>
                    <b-th v-if="isConsignment">{{ $t('label.soldQty') }}</b-th>
                    <b-th v-if="isConsignment">{{ $t('label.restockQty') }}</b-th>
                    <b-th v-if="isConsignment">{{ $t('label.balanceQty') }}</b-th>
                    <b-th v-if="isConsignment">{{ $t('label.starterQty') }}</b-th>
                    <b-th v-if="isConsignment">{{ $t('label.exchangeQty') }}</b-th>
                    <b-th>{{ $t('label.uom') }}</b-th>
                    <b-th>{{ $t('label.unitPrice') }}</b-th>
                    <b-th v-if="canDiscount">{{ $t('salesDoc.lineDiscount') }}</b-th>
                    <b-th v-if="canDiscount" class="text-right">{{ $t('label.amount') }}</b-th>
                    <b-th></b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <template v-for="(line, i) in details">
                    <b-tr :key="`row-${i}`">
                      <b-td style="min-width: 220px;">
                        <b-button
                          variant="outline-secondary"
                          size="sm"
                          block
                          class="text-left line-pick-btn"
                          @click="openItemPicker('line', i)"
                        >
                          <span v-if="line.ItemCode">
                            <span class="font-monospace">{{ line.ItemCode }}</span>
                            <small class="text-muted ml-1">— {{ pickerLabelFor(line.ItemCode).replace(line.ItemCode + ' — ', '') }}</small>
                          </span>
                          <span v-else class="text-muted">
                            <i class="mdi mdi-magnify mr-1"></i>{{ $t('label.select') }}
                          </span>
                        </b-button>
                      </b-td>
                      <b-td v-if="!isConsignment"><b-form-input size="sm" v-model="line.Description" /></b-td>
                      <b-td v-if="!isConsignment"><b-form-input size="sm" type="number" min="0" step="any" v-model.number="line.Qty" @change="applyCustomerPrice(line)" :state="line.Qty != null && Number(line.Qty) < 0 ? false : null" /></b-td>
                      <b-td v-if="isConsignment"><b-form-input size="sm" type="number" v-model.number="line.OpeningQty" readonly class="bg-light text-muted" /></b-td>
                      <b-td v-if="isConsignment"><b-form-input size="sm" type="number" min="0" step="any" v-model.number="line.SoldQty" :state="line.SoldQty != null && Number(line.SoldQty) < 0 ? false : null" /></b-td>
                      <b-td v-if="isConsignment"><b-form-input size="sm" type="number" min="0" step="any" v-model.number="line.AddReturnQty" @change="applyCustomerPrice(line)" :state="line.AddReturnQty != null && Number(line.AddReturnQty) < 0 ? false : null" /></b-td>
                      <b-td v-if="isConsignment">
                        <span :class="balanceFor(line) < 0 ? 'text-danger font-weight-bold' : ''">
                          {{ balanceFor(line) }}
                        </span>
                      </b-td>
                      <b-td v-if="isConsignment">
                        <b-form-input v-if="canExchangeSet.has(line.ItemCode) && Number(line.SoldQty) > 0" size="sm" type="number" min="0" step="any" v-model.number="line.StarterQty" />
                        <span v-else class="text-muted">—</span>
                      </b-td>
                      <b-td v-if="isConsignment">
                        <b-form-input v-if="canExchangeSet.has(line.ItemCode) && Number(line.SoldQty) > 0" size="sm" type="number" min="0" step="any" v-model.number="line.ExchangeQty" />
                        <span v-else class="text-muted">—</span>
                      </b-td>
                      <b-td style="min-width: 130px;">
                        <!-- UoM: dropdown of the item's units (Delivery keeps free text / source). -->
                        <multiselect
                          v-if="!isDelivery"
                          class="line-ms"
                          :value="line.UOM || null"
                          :options="uomOptionsFor(line)"
                          :allow-empty="false"
                          :show-labels="false"
                          open-direction="below"
                          :placeholder="$t('label.uom')"
                          @input="(v) => onUomChange(line, v)"
                        />
                        <b-form-input v-else size="sm" v-model="line.UOM" />
                      </b-td>
                      <b-td style="min-width: 170px;">
                        <!-- Delivery: price follows the source document (read-only). -->
                        <b-form-input
                          v-if="isDelivery"
                          size="sm" type="number"
                          v-model.number="line.UnitPrice"
                          readonly class="bg-light text-muted"
                        />
                        <template v-else>
                          <!-- Pick a standard price tier (Price 1–6) for the chosen UoM.
                               No default; user can't free-type. Quotation gets an editable
                               field after picking; qty may apply the customer's bound price. -->
                          <multiselect
                            class="line-ms"
                            :value="selectedTierFor(line)"
                            :options="priceTierOptionsFor(line)"
                            track-by="value"
                            label="label"
                            :allow-empty="false"
                            :show-labels="false"
                            open-direction="below"
                            :placeholder="$t('label.select')"
                            @input="(t) => onTierChange(line, t)"
                          />
                          <b-form-input
                            v-if="isQuotation"
                            size="sm" type="number" min="0" step="any" class="mt-1"
                            v-model.number="line.UnitPrice"
                            :placeholder="$t('label.unitPrice')"
                            :state="line.UnitPrice != null && Number(line.UnitPrice) < 0 ? false : null"
                          />
                        </template>
                      </b-td>
                      <!-- Line discount: a value plus the unit it is in. Both are
                           the user's statement of it; the API folds them into the
                           single percentage the ledger stores. -->
                      <b-td v-if="canDiscount" style="min-width: 170px;">
                        <b-input-group size="sm" :class="{ 'is-invalid': lineDiscountError(line) }">
                          <b-form-input
                            type="number"
                            min="0"
                            step="any"
                            v-model.number="line.DiscountValue"
                            :disabled="isLocked"
                            :state="lineDiscountError(line) ? false : null"
                          />
                          <b-input-group-append>
                            <b-button
                              v-for="m in discountModes"
                              :key="`ldm-${i}-${m.value}`"
                              size="sm"
                              :variant="line.DiscountMode === m.value ? 'primary' : 'outline-secondary'"
                              :disabled="isLocked"
                              @click="setLineDiscountMode(line, m.value)"
                            >{{ m.text }}</b-button>
                          </b-input-group-append>
                        </b-input-group>
                        <!-- Said under the field being typed in, not as an alert
                             after the form has already been submitted. -->
                        <div v-if="lineDiscountError(line)" class="invalid-feedback d-block small">
                          {{ lineDiscountError(line) }}
                        </div>
                      </b-td>
                      <!-- What this line is worth after its own discount. -->
                      <b-td v-if="canDiscount" class="text-right text-nowrap">
                        <span v-if="lineDiscountAmount(line) > 0">
                          <del class="text-muted small mr-1">{{ lineGross(line).toFixed(2) }}</del>
                          <strong>{{ lineNet(line).toFixed(2) }}</strong>
                        </span>
                        <span v-else>{{ lineGross(line).toFixed(2) }}</span>
                      </b-td>
                      <b-td class="text-nowrap">
                        <b-button
                          v-if="isQuotation"
                          variant="link"
                          class="p-0 mr-2"
                          :class="(line.options && line.options.length) ? 'text-primary' : 'text-muted'"
                          v-b-tooltip.hover
                          :title="$t('quotation.options')"
                          @click="toggleOptions(line)"
                        >
                          <i class="mdi mdi-format-list-bulleted-square"></i>
                          <b-badge v-if="line.options && line.options.length" variant="primary" pill class="align-text-top">{{ line.options.length }}</b-badge>
                        </b-button>
                        <b-button variant="link" class="text-danger p-0" @click="removeLine(i)"><i class="mdi mdi-delete"></i></b-button>
                      </b-td>
                    </b-tr>
                    <!-- Quotation: alternative offers for this item (One Sales-only; the
                         ledger keeps the main line). Totals above use only the main line. -->
                    <b-tr v-if="isQuotation && line.showOptions" :key="`opts-${i}`" class="line-options-row">
                      <b-td :colspan="canDiscount ? 8 : 6" class="pt-1 pb-3 border-0">
                        <div class="line-options">
                          <div class="d-flex justify-content-between align-items-center flex-wrap mb-2">
                            <div>
                              <strong class="small">{{ $t('quotation.optionOf', [line.ItemCode || line.Description || ('#' + (i + 1))]) }}</strong>
                              <small class="text-muted ml-2">{{ optionOfferLabel(line) }}</small>
                              <b-badge v-if="line.isSelected && line.selectedBy" :variant="line.selectedBy === 'customer' ? 'success' : 'info'" class="ml-2">
                                <i class="mdi mdi-check mr-1"></i>{{ selectedByLabel(line.selectedBy) }}
                              </b-badge>
                              <b-button
                                v-else-if="isEdit && line.optionUuid && !isLocked && line.options && line.options.length"
                                size="sm" variant="outline-primary" class="ml-2 py-0"
                                :disabled="optionSelecting"
                                @click="useOption(line, line.optionUuid)"
                              >{{ $t('quotation.useThisOption') }}</b-button>
                            </div>
                            <b-button v-if="!isLocked" variant="outline-primary" size="sm" :disabled="!line.ItemCode" @click="addOption(line)">
                              <i class="mdi mdi-plus"></i> {{ $t('quotation.addOption') }}
                            </b-button>
                          </div>
                          <b-table-simple v-if="line.options && line.options.length" small responsive class="mb-0">
                            <b-thead>
                              <b-tr>
                                <b-th style="width: 40px;">#</b-th>
                                <b-th>{{ $t('label.uom') }}</b-th>
                                <b-th>{{ $t('label.qty') }}</b-th>
                                <b-th>{{ $t('label.unitPrice') }}</b-th>
                                <b-th>{{ $t('label.discount') }}</b-th>
                                <b-th></b-th>
                              </b-tr>
                            </b-thead>
                            <b-tbody>
                              <b-tr v-for="(opt, oi) in line.options" :key="`opt-${i}-${oi}`">
                                <b-td class="text-muted">{{ oi + 1 }}</b-td>
                                <b-td style="min-width: 130px;">
                                  <multiselect
                                    class="line-ms"
                                    :value="opt.UOM || null"
                                    :options="uomOptionsFor(line)"
                                    :allow-empty="false"
                                    :show-labels="false"
                                    :disabled="isLocked"
                                    open-direction="below"
                                    :placeholder="$t('label.uom')"
                                    @input="(v) => onOptionUomChange(opt, v)"
                                  />
                                </b-td>
                                <b-td style="min-width: 90px;">
                                  <b-form-input size="sm" type="number" min="0" step="any" v-model.number="opt.Qty" :disabled="isLocked" :state="opt.Qty != null && Number(opt.Qty) < 0 ? false : null" />
                                </b-td>
                                <b-td style="min-width: 170px;">
                                  <multiselect
                                    class="line-ms"
                                    :value="selectedTierFor(optionCtx(line, opt))"
                                    :options="priceTierOptionsFor(optionCtx(line, opt))"
                                    track-by="value"
                                    label="label"
                                    :allow-empty="false"
                                    :show-labels="false"
                                    :disabled="isLocked"
                                    open-direction="below"
                                    :placeholder="$t('label.select')"
                                    @input="(t) => onOptionTierChange(opt, t)"
                                  />
                                  <b-form-input
                                    size="sm" type="number" min="0" step="any" class="mt-1"
                                    v-model.number="opt.UnitPrice"
                                    :disabled="isLocked"
                                    :placeholder="$t('label.unitPrice')"
                                    :state="opt.UnitPrice != null && Number(opt.UnitPrice) < 0 ? false : null"
                                  />
                                </b-td>
                                <b-td style="min-width: 90px;">
                                  <b-form-input size="sm" type="number" min="0" step="any" v-model.number="opt.Discount" :disabled="isLocked" />
                                </b-td>
                                <b-td class="text-nowrap">
                                  <b-badge v-if="opt.isSelected && opt.selectedBy" :variant="opt.selectedBy === 'customer' ? 'success' : 'info'" class="mr-2">
                                    <i class="mdi mdi-check mr-1"></i>{{ selectedByLabel(opt.selectedBy) }}
                                  </b-badge>
                                  <b-button
                                    v-else-if="isEdit && opt.uuid && !isLocked"
                                    size="sm" variant="outline-primary" class="mr-2 py-0"
                                    :disabled="optionSelecting"
                                    @click="useOption(line, opt.uuid)"
                                  >{{ $t('quotation.useThisOption') }}</b-button>
                                  <b-button v-if="!isLocked" variant="link" class="text-danger p-0" @click="removeOption(line, oi)"><i class="mdi mdi-delete"></i></b-button>
                                </b-td>
                              </b-tr>
                            </b-tbody>
                          </b-table-simple>
                        </div>
                      </b-td>
                    </b-tr>
                    <b-tr v-if="isConsignment && canExchangeSet.has(line.ItemCode) && Number(line.SoldQty) > 0" :key="`tally-${i}`">
                      <b-td colspan="10" class="py-1 border-0">
                        <small :class="tallyOkFor(line) ? 'text-success' : 'text-danger'">
                          {{ tallyOkFor(line)
                              ? `${$t('label.starterSet')} + ${$t('label.exchangeSet')} = Sold ✓`
                              : `${$t('label.starterSet')} + ${$t('label.exchangeSet')} (${(Number(line.StarterQty)||0)+(Number(line.ExchangeQty)||0)}) must equal Sold (${Number(line.SoldQty)||0})` }}
                        </small>
                      </b-td>
                    </b-tr>
                    <b-tr
                      v-if="isConsignment && (Number(line.OpeningQty)||0) > 0 && balanceFor(line) < 0"
                      :key="`negbal-${i}`"
                    >
                      <b-td colspan="10" class="py-1 border-0">
                        <small class="text-danger">
                          <i class="mdi mdi-alert-circle-outline mr-1"></i>Stock cannot go negative — Open ({{ Number(line.OpeningQty)||0 }}) − Sold ({{ Number(line.SoldQty)||0 }}) + Return ({{ Number(line.AddReturnQty)||0 }}) = {{ (Number(line.OpeningQty)||0) - (Number(line.SoldQty)||0) + (Number(line.AddReturnQty)||0) }}. Reduce Sold or increase Return.
                        </small>
                      </b-td>
                    </b-tr>
                  </template>
                </b-tbody>
              </b-table-simple>
              <b-button variant="outline-primary" size="sm" @click="addLine"><i class="mdi mdi-plus"></i> Add line</b-button>

              <!-- Consignment: Discount / Rebate -->
              <div v-if="isConsignment" class="mt-4">
                <div class="d-flex justify-content-between align-items-center">
                  <h6 class="text-primary mb-0">{{ $t('label.discount') }} / {{ $t('label.rebate') }}</h6>
                  <b-button variant="outline-warning" size="sm" @click="addDiscount"><i class="mdi mdi-plus"></i> Add</b-button>
                </div>
                <b-table-simple small responsive class="mt-2" v-if="discounts.length">
                  <b-thead>
                    <b-tr>
                      <b-th>{{ $t('label.itemCode') }}</b-th>
                      <b-th>{{ $t('label.qty') }}</b-th>
                      <b-th>Mode</b-th>
                      <b-th>{{ $t('label.value') }}</b-th>
                      <b-th></b-th>
                    </b-tr>
                  </b-thead>
                  <b-tbody>
                    <b-tr v-for="(d, i) in discounts" :key="i">
                      <b-td style="min-width: 220px;">
                        <b-button
                          variant="outline-secondary"
                          size="sm"
                          block
                          class="text-left line-pick-btn"
                          @click="openItemPicker('discount', i)"
                        >
                          <span v-if="d.ItemCode">
                            <span class="font-monospace">{{ d.ItemCode }}</span>
                            <small class="text-muted ml-1">— {{ pickerLabelFor(d.ItemCode).replace(d.ItemCode + ' — ', '') }}</small>
                          </span>
                          <span v-else class="text-muted">
                            <i class="mdi mdi-magnify mr-1"></i>{{ $t('label.select') }}
                          </span>
                        </b-button>
                      </b-td>
                      <b-td><b-form-input size="sm" type="number" min="0" step="any" v-model.number="d.Qty" :state="d.Qty != null && Number(d.Qty) < 0 ? false : null" /></b-td>
                      <b-td>
                        <multiselect
                          :value="discountModeOption(d.Mode)"
                          :options="discountModes"
                          track-by="value"
                          label="text"
                          :show-labels="false"
                          :searchable="false"
                          :allow-empty="false"
                          @input="(opt) => onDiscountModeChange(d, opt)"
                        />
                      </b-td>
                      <b-td><b-form-input size="sm" type="number" step="0.01" v-model.number="d.Value" /></b-td>
                      <b-td><b-button variant="link" class="text-danger p-0" @click="removeDiscount(i)"><i class="mdi mdi-delete"></i></b-button></b-td>
                    </b-tr>
                  </b-tbody>
                </b-table-simple>
              </div>

              <!-- Inline signatures (Customer + Sales Agent) — for every doc type. -->
              <div class="mt-4">
                <h6 class="text-primary">{{ $t('signature.title') }}</h6>
                <p class="text-muted small">{{ $t('signature.hint') }}</p>
                <b-row>
                  <b-col cols="12" md="6">
                    <div class="sig-card">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <strong><i class="mdi mdi-account-outline mr-1"></i>{{ $t('signature.customer') }}</strong>
                        <b-badge v-if="sigCustomerExisting" variant="success">
                          <i class="mdi mdi-check"></i> {{ $t('signature.signed') }}
                        </b-badge>
                        <b-badge v-else variant="secondary">{{ $t('signature.pending') }}</b-badge>
                      </div>
                      <div v-if="sigCustomerExisting">
                        <small v-if="sigCustomerName" class="text-muted d-block mb-1">{{ sigCustomerName }}</small>
                        <img :src="'data:image/png;base64,' + sigCustomerExisting" class="existing-sig" alt="Customer signature" />
                      </div>
                      <div v-else class="customer-sig-placeholder">
                        <i class="mdi mdi-share-variant mb-1"></i>
                        <div class="small">{{ $t('signature.customerHint') }}</div>
                      </div>
                    </div>
                  </b-col>
                  <b-col cols="12" md="6">
                    <div class="sig-card">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <strong><i class="mdi mdi-account-tie-outline mr-1"></i>{{ $t('signature.salesAgent') }}</strong>
                        <b-badge v-if="sigAgentExisting && !sigAgentData" variant="success">
                          <i class="mdi mdi-check"></i> {{ $t('signature.signed') }}
                        </b-badge>
                      </div>
                      <b-form-input v-model="sigAgentName" size="sm" :placeholder="$t('signature.namePlaceholder')" class="mb-2" />
                      <img v-if="sigAgentExisting && !sigAgentData" :src="'data:image/png;base64,' + sigAgentExisting" class="existing-sig" alt="Sales agent signature" />
                      <SignaturePad v-else ref="sigAgentPad" :height="140" @change="(d) => sigAgentData = d" />
                      <b-button v-if="sigAgentExisting && !sigAgentData" size="sm" variant="link" class="text-warning p-0 mt-1" @click="sigAgentExisting = ''">
                        <i class="mdi mdi-pencil mr-1"></i>{{ $t('signature.reSign') }}
                      </b-button>
                    </div>
                  </b-col>
                </b-row>
              </div>

              <!-- Totals -->
              <div class="card bg-light mt-4">
                <div class="card-body py-3">
                  <div class="d-flex justify-content-between">
                    <span>{{ $t('label.subtotal') }}</span><strong>{{ subTotal.toFixed(2) }}</strong>
                  </div>
                  <div v-if="isConsignment && discountTotal > 0" class="d-flex justify-content-between text-warning">
                    <span>{{ $t('label.discount') }}</span><strong>−{{ discountTotal.toFixed(2) }}</strong>
                  </div>

                  <!-- Line discounts, summed. Shown only once there are any, so
                       a document without discounts looks exactly as it did. -->
                  <div v-if="canDiscount && lineDiscountTotal > 0" class="d-flex justify-content-between text-warning">
                    <span>{{ $t('label.discount') }} <small class="text-muted">({{ $t('label.item') }})</small></span>
                    <strong>−{{ lineDiscountTotal.toFixed(2) }}</strong>
                  </div>

                  <!-- One discount over the whole document, on what the line
                       discounts left. Always editable, so it can be cleared. -->
                  <div v-if="canDiscount" class="d-flex justify-content-between align-items-center mt-1">
                    <span>
                      {{ $t('salesDoc.docDiscount') }}
                      <i
                        v-b-tooltip.hover
                        :title="$t('salesDoc.docDiscountHint')"
                        class="mdi mdi-information-outline text-muted ml-1"
                      ></i>
                    </span>
                    <div class="d-flex align-items-center">
                      <b-input-group size="sm" style="width: 160px;" :class="{ 'is-invalid': docDiscountError() }">
                        <b-form-input
                          type="number"
                          min="0"
                          step="any"
                          v-model.number="header.DocDiscountValue"
                          :disabled="isLocked"
                          :state="docDiscountError() ? false : null"
                        />
                        <b-input-group-append>
                          <b-button
                            v-for="m in discountModes"
                            :key="`ddm-${m.value}`"
                            size="sm"
                            :variant="header.DocDiscountMode === m.value ? 'primary' : 'outline-secondary'"
                            :disabled="isLocked"
                            @click="setDocDiscountMode(m.value)"
                          >{{ m.text }}</b-button>
                        </b-input-group-append>
                      </b-input-group>
                      <strong v-if="docDiscountAmount > 0 && !docDiscountError()" class="text-warning ml-2 text-nowrap">
                        −{{ docDiscountAmount.toFixed(2) }}
                      </strong>
                    </div>
                  </div>
                  <div v-if="canDiscount && docDiscountError()" class="text-right">
                    <div class="invalid-feedback d-block small">{{ docDiscountError() }}</div>
                  </div>

                  <!-- What the customer is actually being charged before tax. -->
                  <div
                    v-if="canDiscount && totalDiscount > 0"
                    class="d-flex justify-content-between mt-1"
                  >
                    <span>{{ $t('label.afterDiscount') }}</span>
                    <strong>{{ (subTotal - totalDiscount).toFixed(2) }}</strong>
                  </div>

                  <!-- Auto-listed taxes — every active non-zero AutoCount tax code,
                       each applies to (subtotal − discount). No manual add/remove. -->
                  <div v-for="t in autoTaxes" :key="`tax-${t.code}`" class="d-flex justify-content-between">
                    <span>{{ $t('label.tax') }} <small class="text-muted">({{ t.code }} {{ t.rate }}%)</small></span>
                    <strong>{{ t.amount.toFixed(2) }}</strong>
                  </div>

                  <hr class="my-2" />
                  <div class="d-flex justify-content-between">
                    <strong>{{ $t('label.grandTotal') }} ({{ header.CurrencyCode }})</strong>
                    <strong class="text-primary">{{ grandTotal.toFixed(2) }}</strong>
                  </div>
                </div>
              </div>

              <!--
                How the customer paid.
                Part of the form, not a dialog after saving: under manual
                approval a cash sale produces an approval request and no
                document, so a question asked afterwards was never asked at all.
              -->
              <div v-if="isCashSale" class="mt-4">
                <h6 class="text-primary">{{ $t('salesDoc.paymentTitle') }}</h6>

                <b-alert v-if="paymentMethodsLoaded && !paymentMethods.length" show variant="warning" class="small">
                  {{ $t('salesDoc.paymentNoMethods') }}
                </b-alert>

                <b-row v-else>
                  <b-col cols="12">
                    <b-form-group :label="$t('module.paymentMethod')" :description="$t('salesDoc.paymentOptionalHint')">
                      <multiselect
                        :value="selectedPaymentMethod"
                        :options="paymentMethodOptions"
                        track-by="uuid"
                        label="name"
                        :show-labels="false"
                        open-direction="below"
                        :placeholder="$t('label.select')"
                        :disabled="isLocked"
                        @input="onPaymentMethodChange"
                      />
                    </b-form-group>
                  </b-col>

                  <!-- What the customer needs to see to pay: the account details
                       and the QR to scan. Straight off the method, so finance
                       owns the content. -->
                  <b-col v-if="selectedPaymentMethod" cols="12">
                    <div class="card bg-light mb-3">
                      <div class="card-body py-2 px-3">
                        <div v-for="(d, di) in selectedPaymentMethod.details" :key="`pd-${di}`" class="small">
                          <span class="text-muted">{{ d.label }}:</span> <strong>{{ d.value }}</strong>
                        </div>
                        <p v-if="selectedPaymentMethod.instructions" class="small text-muted mb-0 mt-1">
                          {{ selectedPaymentMethod.instructions }}
                        </p>
                        <div v-if="selectedPaymentMethod.imageUrl" class="text-center mt-2">
                          <img
                            :src="selectedPaymentMethod.imageUrl"
                            style="max-height: 200px; max-width: 100%; object-fit: contain;"
                            :alt="selectedPaymentMethod.name"
                          />
                        </div>
                        <p v-if="paymentIsCash" class="small text-success mb-0 mt-2">
                          <i class="mdi mdi-wallet-outline mr-1"></i>{{ $t('salesDoc.paymentCashNote') }}
                        </p>
                      </div>
                    </div>
                  </b-col>

                  <b-col v-if="selectedPaymentMethod" cols="12">
                    <b-form-group :label="$t('salesDoc.paymentAmount')" :description="$t('salesDoc.paymentAmountHint')">
                      <b-form-input
                        v-model.number="paymentAmount"
                        type="number"
                        min="0"
                        step="0.01"
                        :placeholder="grandTotal.toFixed(2)"
                        :disabled="isLocked"
                      />
                    </b-form-group>
                  </b-col>

                  <b-col v-if="selectedPaymentMethod" cols="12">
                    <b-form-group
                      :label="$t('salesDoc.paymentReference')"
                      :description="$t('salesDoc.paymentReferenceHint')"
                    >
                      <b-form-input v-model="paymentReference" :disabled="isLocked" />
                    </b-form-group>
                  </b-col>

                  <b-col v-if="selectedPaymentMethod" cols="12">
                    <b-form-group :label="$t('salesDoc.paymentProof')">
                      <b-form-file
                        accept="image/*"
                        :placeholder="$t('label.select')"
                        :disabled="isLocked"
                        @change="onPaymentProofSelected"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>

              <!-- Quotation pricing binding — choose how these prices carry forward. -->
              <div v-if="isQuotation" class="mt-4">
                <h6 class="text-primary">{{ $t('quotation.priceBindingTitle') }}</h6>
                <p class="text-muted small mb-2">{{ $t('quotation.priceBindingHint') }}</p>
                <b-form-group>
                  <b-form-radio v-model="priceBinding" value="once" :disabled="isLocked">
                    <strong>{{ $t('quotation.bindingOnce') }}</strong>
                    <small class="text-muted d-block">{{ $t('quotation.bindingOnceDesc') }}</small>
                  </b-form-radio>
                  <b-form-radio v-model="priceBinding" value="always" :disabled="isLocked" class="mt-2">
                    <strong>{{ $t('quotation.bindingAlways') }}</strong>
                    <small class="text-muted d-block">{{ $t('quotation.bindingAlwaysDesc') }}</small>
                  </b-form-radio>
                  <b-form-radio v-model="priceBinding" value="min_qty" :disabled="isLocked" class="mt-2">
                    <strong>{{ $t('quotation.bindingMinQty') }}</strong>
                    <small class="text-muted d-block">{{ $t('quotation.bindingMinQtyDesc') }}</small>
                  </b-form-radio>
                </b-form-group>
                <!-- Expiry for a carried-forward binding. Hidden for once-off: nothing to expire. -->
                <b-row v-if="priceBinding !== 'once'">
                  <b-col cols="12">
                    <b-form-group :label="$t('quotation.priceBindingUntil')" :description="$t('quotation.priceBindingUntilHint')">
                      <b-form-input type="date" v-model="priceBindingUntil" :min="today" :disabled="isLocked" />
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>

              <b-alert v-if="validationErrors.length" variant="danger" show class="mt-3 mb-0">
                <ul class="mb-0">
                  <li v-for="(e, i) in validationErrors" :key="i">{{ e }}</li>
                </ul>
              </b-alert>

              <div class="text-right mt-3">
                <b-button variant="light" class="mr-2" @click="cancel">{{ $t('label.cancel') }}</b-button>
                <b-button
                  v-if="!isLocked && $can(isPending ? 'Update Sales Document' : (isEdit ? 'Update Sales Document' : 'Create Sales Document'))"
                  type="submit"
                  variant="primary"
                  :disabled="submitting || hasDiscountProblem"
                  :title="hasDiscountProblem ? discountProblems[0].message : ''"
                >{{ $t('label.submit') }}</b-button>
              </div>

              <!-- The decision, on the document itself. Approve posts exactly
                   what is on screen; an edit above is saved to the queue by
                   Submit first. -->
              <div v-if="canDecide" class="mt-4 pt-3 border-top">
                <b-form-group :label="$t('label.remarks')">
                  <b-form-textarea v-model="decisionRemarks" rows="2" :class="{ 'is-invalid': decisionError }" />
                  <div v-if="decisionError" class="invalid-feedback d-block">{{ $t("message.requiredX", [$t("label.rejectionReason")]) }}</div>
                </b-form-group>
                <div class="text-right">
                  <b-button variant="danger" class="mr-2" @click="rejectHeld" :disabled="deciding || submitting">
                    <i class="mdi mdi-close mr-1"></i>{{ $t('label.reject') }}
                  </b-button>
                  <b-button variant="success" @click="approveHeld" :disabled="deciding || submitting">
                    <i class="mdi mdi-check mr-1"></i>{{ $t('label.approveAndPost') }}
                  </b-button>
                </div>
              </div>
            </b-form>
            </b-overlay>
          </div>
        </div>

        <!-- Sub-company quotations derived from this quotation -->
        <div v-if="isEdit && isQuotation && subQuotations.length" class="card">
          <div class="card-body">
            <h5 class="mb-3">{{ $t('companyProfile.subQuotations') }}</h5>
            <div v-for="sub in subQuotations" :key="sub.uuid" class="border rounded p-3 mb-3">
              <div class="d-flex justify-content-between align-items-center flex-wrap">
                <div>
                  <strong>{{ sub.company_profile ? sub.company_profile.name : '' }}</strong>
                  <span class="text-muted ml-2">{{ sub.doc_no }}</span>
                  <b-badge v-if="sub.is_manually_adjusted" variant="warning" class="ml-2">
                    {{ $t('companyProfile.manuallyAdjusted') }}
                  </b-badge>
                  <b-badge v-else variant="light" class="ml-2 border">
                    {{ $t('companyProfile.followsSource') }} (+{{ sub.markup_percent }}%)
                  </b-badge>
                </div>
                <div>
                  <strong class="text-primary mr-3">{{ sub.currency_code }} {{ Number(sub.total).toFixed(2) }}</strong>
                  <b-button
                    v-if="$can('Update Sub Quotation') && subEditingUuid !== sub.uuid"
                    variant="outline-secondary"
                    size="sm"
                    class="mr-2"
                    @click="startEditSub(sub)"
                  >
                    <i class="mdi mdi-pencil mr-1"></i>{{ $t('companyProfile.editPrices') }}
                  </b-button>
                  <b-button variant="success" size="sm" @click="downloadSubPdf(sub)">
                    <i class="mdi mdi-file-pdf-box mr-1"></i>PDF
                  </b-button>
                </div>
              </div>

              <div v-if="subEditingUuid === sub.uuid" class="mt-3">
                <b-table-simple small responsive>
                  <b-thead>
                    <b-tr>
                      <b-th>{{ $t('label.itemCode') }}</b-th>
                      <b-th>{{ $t('label.description') }}</b-th>
                      <b-th class="text-right" style="width: 110px;">{{ $t('label.quantity') }}</b-th>
                      <b-th class="text-right" style="width: 140px;">{{ $t('label.unitPrice') }}</b-th>
                      <b-th class="text-right" style="width: 120px;">{{ $t('label.subTotal') }}</b-th>
                    </b-tr>
                  </b-thead>
                  <b-tbody>
                    <b-tr v-for="(l, li) in subEditLines" :key="li">
                      <b-td>{{ l.item_code }}</b-td>
                      <b-td>{{ l.description }}</b-td>
                      <b-td class="text-right">{{ l.qty }}</b-td>
                      <b-td>
                        <b-form-input
                          type="number"
                          min="0"
                          step="0.01"
                          size="sm"
                          class="text-right"
                          v-model.number="l.unit_price"
                        />
                      </b-td>
                      <b-td class="text-right">
                        {{ ((Number(l.qty) || 0) * (Number(l.unit_price) || 0)).toFixed(2) }}
                      </b-td>
                    </b-tr>
                  </b-tbody>
                </b-table-simple>
                <div class="text-right">
                  <span class="text-muted mr-3">{{ $t('label.subTotal') }}: {{ subEditTotal().toFixed(2) }}</span>
                  <b-button variant="light" size="sm" class="mr-2" @click="cancelEditSub">
                    {{ $t('label.cancel') }}
                  </b-button>
                  <b-button variant="primary" size="sm" :disabled="subSaving" @click="saveSub(sub)">
                    {{ $t('label.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="itemPickerModal" :title="$t('label.itemCode')" hide-footer size="lg">
      <b-form-input
        v-model="pickerSearch"
        :placeholder="$t('label.search')"
        autofocus
        class="mb-3"
      />
      <div v-if="pickerFilteredItems.length" class="picker-list">
        <div
          v-for="it in pickerFilteredItems"
          :key="it.item_code"
          class="picker-row"
          @click="pickPickerItem(it)"
        >
          <div class="font-monospace text-primary">{{ it.item_code }}</div>
          <small class="text-muted">{{ it.description || '—' }}</small>
        </div>
      </div>
      <div v-else class="text-muted text-center py-4">
        {{ $t('label.search') }} — no items match.
      </div>
    </b-modal>

  </Layout>
</template>

<style scoped>
.line-options {
  border: 1px dashed #d9d4ff;
  border-radius: 8px;
  padding: 10px 12px;
  background: #faf9ff;
}
.line-pick-btn {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.picker-list {
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
}
.picker-row {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}
.picker-row:last-child { border-bottom: 0; }
.picker-row:hover { background-color: #f7f5ff; }
.sig-card {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 12px;
  background: #fafafa;
}
.existing-sig {
  display: block;
  max-width: 100%;
  height: 140px;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 6px;
  object-fit: contain;
}
.customer-sig-placeholder {
  text-align: center;
  color: #6c757d;
  padding: 30px 12px;
  border: 1px dashed #ced4da;
  border-radius: 6px;
  background: #fff;
}
.customer-sig-placeholder i { font-size: 28px; display: block; }
::v-deep .b-table-simple td,
::v-deep .b-table-simple th { overflow: visible; }
::v-deep .multiselect { min-height: 32px; }
::v-deep .multiselect__tags {
  min-height: 32px;
  padding: 4px 40px 0 8px;
  font-size: 0.85rem;
}
::v-deep .multiselect__single,
::v-deep .multiselect__placeholder {
  font-size: 0.85rem;
  padding: 2px 0 0;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
::v-deep .multiselect__select { height: 32px; }
::v-deep .multiselect__content-wrapper {
  z-index: 50;
  max-height: 240px;
  border: 1px solid #ced4da;
}
::v-deep .multiselect--active { z-index: 60; }
::v-deep .multiselect__option {
  min-height: 32px;
  padding: 6px 10px;
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
}

/* Align inputs + dropdowns on each Lines row: same height, vertically centred. */
::v-deep .b-table-simple td,
::v-deep .b-table-simple th { vertical-align: middle; }
::v-deep .b-table-simple .form-control,
::v-deep .b-table-simple .form-control-sm {
  height: 34px;
  font-size: 0.85rem;
}
/* Compact the line dropdowns to match the inputs. !important is needed because
   bootstrap-vue sizes .multiselect__tags with !important. */
::v-deep .line-ms .multiselect,
::v-deep .line-ms .multiselect__tags {
  min-height: 34px !important;
  height: 34px !important;
}
::v-deep .line-ms .multiselect__tags {
  padding: 6px 30px 0 8px !important;
  font-size: 0.85rem !important;
  display: flex;
  align-items: center;
}
::v-deep .line-ms .multiselect__single,
::v-deep .line-ms .multiselect__placeholder {
  margin-bottom: 0 !important;
  padding-top: 0 !important;
  line-height: 1.2;
  font-size: 0.85rem !important;
}
::v-deep .line-ms .multiselect__select { height: 32px !important; }

/* Stop the responsive table wrapper from clipping the open dropdown. */
::v-deep .table-responsive { overflow: visible !important; }
</style>
